<template>
  <div v-if="loading" class="loading-overlay">
 <div class="spinner"></div>
 </div>

 <div v-else-if="error">
     {{ paymentError || "Une erreur est survenue lors du chargement des détails de l'offre." }}
 </div>
 <div v-else>
  <div v-if="loadingPayment" class="loading-overlay">
    <div class="spinner"></div>
  </div>
  <!-- ===================================================================
       APPLICATION MOBILE — tunnel de réservation
       Arbre de rendu entièrement distinct de celui du site : la feuille
       style.css du site cible des éléments nus (header, h1, p, button, img)
       et rattraperait n'importe quel balisage neuf. Le site est servi
       inchangé par le <template v-else> plus bas.
       =================================================================== -->
  <div v-if="isNative" class="rv-page">

    <!-- ÉTAPE 1 — MA RÉSERVATION -->
    <template v-if="!showReservationForm && !paymentSuccess">
      <div class="rv-step">
        <span class="rv-step__num">1. Ma réservation</span>
        <span class="rv-step__tag">Configuration</span>
      </div>

      <header class="rv-offer">
        <span class="rv-offer__sport">{{ rvOd.sport }}<template v-if="rvOd.type"> · {{ rvOd.type }}</template></span>
        <h1 class="rv-offer__title" v-html="rvTitre"></h1>
        <span class="rv-offer__meta">
          {{ rvOd.finalCity }}<template v-if="rvOd.finalPostcode"> ({{ rvOd.finalPostcode }})</template>
          <template v-if="offerDetails.price"> · {{ rvEuro(offerDetails.price) }} € / pers.</template>
        </span>
      </header>

      <!-- 0. Rappel de la date. Ce n'est pas un choix : la date est arrêtée
           sur la fiche d'activité, qui montre places et tarif par date. Le
           bouton « Date » de l'ancien tunnel n'était relié à rien. -->
      <div class="rv-when">
        <div class="rv-when__main">
          <p class="rv-when__date">{{ isStageSejour() ? 'Du' : 'Le' }} {{ formattedDates }} {{ findScheduleByDate(dateSelected) }}</p>
          <p class="rv-when__addr">{{ rvAdresse }}</p>
        </div>
        <RouterLink class="rv-when__edit" :to="rvRetourFiche">Modifier</RouterLink>
      </div>

      <!-- 1. Critère de l'offre : « Niveaux », « Horaire »… Il conditionne
           le stock et le tarif, il reste donc dans le tunnel. -->
      <section v-if="rvCritereNom" class="rv-card" :class="{ 'is-error': showErrorAnimationVariations }">
        <p class="rv-card__label">{{ rvCritereNom }}<span class="rv-card__req">Obligatoire</span></p>
        <button
          type="button"
          class="rv-select"
          :class="{ 'is-empty': !variations }"
          @click="openCriterionModal(selectedDetail)"
        >
          <span v-if="variations" v-html="displayVariations"></span>
          <span v-else>Sélectionner</span>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
        </button>
      </section>

      <!-- 2. Quantité -->
      <section v-if="selectedDetail" class="rv-card" :class="{ 'is-error': showErrorAnimationQuantity }">
        <p class="rv-card__label">
          Nombre de participants
          <span v-if="rvStock" class="rv-card__req">Max. {{ rvStock }}</span>
        </p>
        <div class="rv-line">
          <span class="rv-line__text">
            <span class="rv-line__name">Places à réserver</span>
            <span class="rv-line__sub">{{ rvEuro(offerDetails.price) }} € / personne</span>
          </span>
          <span class="rv-step2">
            <button
              type="button"
              class="rv-step2__btn"
              :disabled="quantity <= 0 || rvVerrouParCritere"
              @click="quantity = Math.max(quantity - 1, 0); calculateTotal()"
              aria-label="Retirer une place"
            >–</button>
            <span class="rv-step2__val">{{ quantity || 0 }}</span>
            <button
              type="button"
              class="rv-step2__btn"
              :disabled="quantity >= rvStock || rvVerrouParCritere"
              @click="quantity = Math.min(quantity + 1, rvStock); calculateTotal()"
              aria-label="Ajouter une place"
            >+</button>
          </span>
        </div>
        <p v-if="rvVerrouParCritere" class="rv-card__hint">{{ rvCritereNom }} : à choisir avant le nombre de places.</p>
        <div v-else class="rv-sub">
          <span>Sous-total participants</span>
          <span class="rv-sub__val">{{ rvEuro(offerDetails.price * (quantity || 0)) }} €</span>
        </div>
      </section>

      <!-- 3. Options payantes. Absentes de la plupart des offres. -->
      <section v-if="selectedDetail && rvOptions.length" class="rv-card">
        <p class="rv-card__label">Options complémentaires</p>
        <div class="rv-line" v-for="opt in rvOptions" :key="opt.name">
          <span class="rv-line__text">
            <span class="rv-line__name">{{ opt.name }}</span>
            <span class="rv-line__sub">+ {{ rvEuro(opt.price) }} € / personne</span>
          </span>
          <span class="rv-step2">
            <button
              type="button"
              class="rv-step2__btn"
              :disabled="(equipmentQuantities[opt.name]?.quantity || 0) <= 0"
              @click="updateQuantity(opt.name, Math.max((equipmentQuantities[opt.name]?.quantity || 0) - 1, 0), opt.price)"
              :aria-label="'Retirer : ' + opt.name"
            >–</button>
            <span class="rv-step2__val">{{ equipmentQuantities[opt.name]?.quantity || 0 }}</span>
            <button
              type="button"
              class="rv-step2__btn"
              :disabled="(equipmentQuantities[opt.name]?.quantity || 0) >= quantity"
              @click="updateQuantity(opt.name, Math.min((equipmentQuantities[opt.name]?.quantity || 0) + 1, quantity), opt.price)"
              :aria-label="'Ajouter : ' + opt.name"
            >+</button>
          </span>
        </div>
      </section>

      <!-- 4. Assurance annulation. Le composant porte son propre rendu natif. -->
      <section v-if="selectedDetail && quantity > 0 && insuranceDates.depart" class="rv-card rv-card--ins">
        <ChapkaInsurance
          :offer-type="rvOd.type"
          :offer-id="offerDetails.offerId"
          :participant-count="quantity"
          :start-date="insuranceDates.depart"
          :end-date="insuranceDates.retour"
          :total-amount="offerDetails.price * quantity"
          destination="FR"
          @update:insurance="handleInsuranceUpdate"
          @update:participants="handleParticipantsUpdate"
          @quote-loaded="handleQuoteLoaded"
        />
      </section>

      <!-- 5. Total -->
      <section v-if="selectedDetail" class="rv-card rv-total">
        <div class="rv-total__row">
          <span class="rv-total__label">Total estimé</span>
          <span class="rv-total__val">{{ rvEuro(totalPrice) }} €</span>
        </div>
        <p v-if="insuranceAccepted && insurancePremium > 0" class="rv-total__note">
          dont assurance Cap Explorer : {{ rvEuro(insurancePremium) }} €
        </p>
      </section>

      <!-- 6. CGV -->
      <div v-if="selectedDetail" class="rv-cgv" :class="{ 'is-error': showErrorAnimationCGV }">
        <!-- Le lien vit hors du <label> : à l'intérieur, la moitié de la
             zone à taper pour accepter ouvrait la page des CGV. -->
        <label class="rv-cgv__label">
          <input type="checkbox" v-model="accepteCGV" class="rv-cgv__input">
          <span class="rv-cgv__box" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"><path d="m5 13 4 4L19 7"/></svg>
          </span>
          <span class="rv-cgv__text">J'accepte les conditions générales de vente</span>
        </label>
        <RouterLink to="/CGU" class="rv-cgv__link">Lire les CGV OuiCSport</RouterLink>
      </div>

      <!-- 7. Barre d'action -->
      <div v-if="selectedDetail" class="rv-bar">
        <span class="rv-bar__price">
          <span class="rv-bar__label">Total estimé</span>
          <span class="rv-bar__val">{{ rvEuro(totalPrice) }} €</span>
        </span>
        <button
          type="button"
          class="rv-bar__cta"
          :disabled="!rvPeutReserver"
          @click="reserver"
        >Réserver</button>
      </div>
    </template>

    <!-- ÉTAPE 2 — PAIEMENT -->
    <template v-else-if="showReservationForm && !paymentSuccess">
      <div class="rv-step">
        <span class="rv-step__num">2. Paiement</span>
        <span class="rv-step__tag">Règlement</span>
      </div>

      <div class="rv-when rv-when--flat">
        <div class="rv-when__main">
          <p class="rv-when__date">{{ isStageSejour() ? 'Du' : 'Le' }} {{ formattedDates }} {{ findScheduleByDate(dateSelected) }}</p>
          <a class="rv-when__addr rv-when__addr--link"
             :href="'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(rvAdresse)"
             target="_blank" rel="noopener">{{ rvAdresse }}</a>
        </div>
      </div>

      <!-- 1. Récapitulatif -->
      <section class="rv-card">
        <p class="rv-card__label">Récapitulatif</p>
        <div class="rv-recap">
          <div class="rv-recap__row">
            <span>{{ quantity }} × {{ rvOd.sport }}<template v-if="!noCritrion && displayVariations"> — {{ displayVariations }}</template></span>
            <span>{{ rvEuro(offerDetails.price * quantity) }} €</span>
          </div>
          <div class="rv-recap__row" v-for="(eq, nom) in equipmentQuantities" :key="nom">
            <span>{{ eq.quantity }} × {{ nom }}</span>
            <span>{{ rvEuro(eq.price * eq.quantity) }} €</span>
          </div>
          <div class="rv-recap__row" v-if="insuranceAccepted && insurancePremium > 0">
            <span>Assurance Cap Explorer <span class="rv-chip">Inclus</span></span>
            <span>{{ rvEuro(insurancePremium) }} €</span>
          </div>
          <div class="rv-recap__row rv-recap__row--total">
            <span>Total de la commande</span>
            <span>{{ rvEuro(totalPrice) }} €</span>
          </div>
        </div>
      </section>

      <!-- 2. Code promo -->
      <section class="rv-card">
        <p class="rv-card__label">Code promo</p>
        <div class="rv-promo">
          <input
            type="text"
            class="rv-promo__input"
            placeholder="CODE PROMO"
            v-model="promoCode"
            :disabled="!isLoggedIn"
            autocapitalize="characters"
            autocomplete="off"
          >
          <button
            type="button"
            class="rv-promo__btn"
            :disabled="!isLoggedIn"
            @click="voucherActivateFront(promoCode)"
          >Valider</button>
        </div>
        <p v-if="!isLoggedIn" class="rv-card__hint">
          <RouterLink :to="{ name: 'LoginPage' }" class="rv-cgv__link">Connectez-vous</RouterLink>
          pour utiliser un code promo.
        </p>
        <p v-if="voucherError" class="rv-msg rv-msg--ko">{{ voucherError }}</p>
        <p v-if="voucherMessage" class="rv-msg rv-msg--ok">{{ voucherMessage }}</p>
      </section>

      <!-- 3. Crédits -->
      <section class="rv-credits">
        <span class="rv-credits__text">
          <span class="rv-credits__label">Solde OuiCSport</span>
          <span class="rv-credits__val">{{ rvEuro(creditsBalance) }} €</span>
        </span>
        <button
          type="button"
          class="rv-credits__btn"
          :disabled="creditsBalance <= 0 || creditsToUse > 0"
          @click="useCredits"
        >{{ creditsToUse > 0 ? 'Appliqué' : 'Utiliser' }}</button>
      </section>

      <!-- 4. Carte bancaire. Le champ est un iframe injecté par Stripe dans
           #card-element : seul le conteneur est stylable. -->
      <section class="rv-card" v-show="!isKlarnaSelected">
        <p class="rv-card__label">
          Carte bancaire
          <span class="rv-cards"><span>CB</span><span>VISA</span><span>MC</span></span>
        </p>
        <p class="rv-due">Montant restant à payer : <strong>{{ rvEuro(remainingAmount) }} €</strong></p>
        <div id="card-element" class="stripe-element rv-stripe"></div>
      </section>

      <!-- Klarna : le paiement en 3× n'est pas encore ouvert dans l'app. La
           redirection repart vers `window.location.href`, soit
           https://localhost/… dans la webview : l'utilisateur quitterait
           l'application et le retour de paiement n'aboutirait jamais. En natif,
           le bouton reste visible mais ouvre une feuille « en attente de mise
           en prod » au lieu de basculer le mode de paiement. -->
      <section class="rv-card" v-if="isKlarnaSelected && !isNative">
        <p class="rv-klarna-msg">
          En sélectionnant « Payer », vous serez redirigé vers Klarna pour finaliser
          votre paiement en toute sécurité.
        </p>
      </section>
      <button
        v-if="isStageSejour()"
        type="button"
        class="rv-klarna"
        :class="{ 'is-on': isKlarnaSelected }"
        @click="isNative ? (showKlarnaSoon = true) : togglePaymentMethod()"
      >
        <span class="rv-klarna__head">
          <span class="rv-klarna__title">{{ isKlarnaSelected ? 'Payer en 1 fois' : 'Paiement en 3× sans frais' }}</span>
          <span class="rv-klarna__badge">Klarna</span>
        </span>
        <span class="rv-klarna__sub">Échelonnez votre règlement en trois mensualités.</span>
      </button>

      <!-- 5. Erreur de paiement -->
      <p v-if="paymentError" class="rv-error">{{ paymentError }}</p>

      <!-- 6. Barre d'action -->
      <div class="rv-bar">
        <span class="rv-bar__price">
          <span class="rv-bar__label">À payer</span>
          <span class="rv-bar__val">{{ rvEuro(remainingAmount) }} €</span>
        </span>
        <button
          type="button"
          class="rv-bar__cta"
          :class="{ 'is-loading': loadingPayment }"
          :disabled="loadingPayment"
          @click="submitPayment"
        >
          <span v-if="loadingPayment" class="rv-bar__spin" aria-hidden="true"></span>
          {{ loadingPayment ? 'Paiement en cours…' : 'Payer' }}
        </button>
      </div>
    </template>

    <!-- Feuille de choix du critère -->
    <div v-if="showCriterionModal" class="rv-sheet" @click.self="showCriterionModal = false">
      <div class="rv-sheet__panel" role="dialog" aria-modal="true">
        <span class="rv-sheet__grip" aria-hidden="true"></span>
        <p class="rv-sheet__title">{{ rvCritereNom }}</p>
        <ul class="rv-sheet__list">
          <li v-for="(option, index) in modalOptions" :key="index">
            <!-- Le stock peut être NÉGATIF quand un créneau a été surréservé
                 (l'API renvoie « -4 »). Comparer à zéro laissait alors la
                 ligne active et affichait « -4 places ». -->
            <button
              type="button"
              class="rv-sheet__opt"
              :class="{ 'is-full': rvPlaces(selectedDetail[index]) <= 0 }"
              :disabled="rvPlaces(selectedDetail[index]) <= 0"
              @click="selectCriterionOption(option)"
            >
              <span v-html="option.criterion.map(c => c.Value).join(' / ')"></span>
              <span class="rv-sheet__state">
                {{ rvPlaces(selectedDetail[index]) <= 0
                  ? '(complet)'
                  : rvPlaces(selectedDetail[index]) + ' place' + (rvPlaces(selectedDetail[index]) > 1 ? 's' : '') }}
              </span>
            </button>
          </li>
        </ul>
      </div>
    </div>

    <!-- Feuille « Klarna bientôt disponible » (natif uniquement) -->
    <div v-if="showKlarnaSoon" class="rv-sheet" @click.self="showKlarnaSoon = false">
      <div class="rv-sheet__panel" role="dialog" aria-modal="true">
        <span class="rv-sheet__grip" aria-hidden="true"></span>
        <p class="rv-sheet__title">Paiement en 3× avec Klarna</p>
        <p class="rv-klarna-msg">
          Cette option est en attente de mise en production dans l'application.
          Elle est disponible dès maintenant sur www.ouicsport.fr.
        </p>
        <button type="button" class="rv-bar__cta rv-sheet__close" @click="showKlarnaSoon = false">Compris</button>
      </div>
    </div>
  </div>

  <!-- ===================================================================
       SITE WEB — balisage d'origine, inchangé.
       =================================================================== -->
  <template v-else>
  <section class="hero3"
           :style="offerDetails.offerDetails.sport
                   ? `background: url('${sportBanner(offerDetails.offerDetails.sport)}') no-repeat center 50%/cover;`
                   : ''">
    </section>

   <div class="activity-details">
     <div v-if="isNative" style="position: absolute; top: 12px; right: 12px; z-index: 20;">
       <button type="button" @click="onShare" aria-label="Partager"
               style="width: 40px; height: 40px; border-radius: 50%; border: none; background: rgba(255,255,255,0.92); color: #333; font-size: 18px; cursor: pointer; box-shadow: 0 2px 6px rgba(0,0,0,0.15);">
         <i class="fa fa-share-alt"></i>
       </button>
     </div>
     <div class="activity-details-top" :style="{ gridTemplateColumns: !isMenuVisible ? '1fr 2fr 1fr' : 'none' }">
         <h2 v-if="!isMenuVisible">{{offerDetails.offerDetails.sport}} <br v-if="!isMenuVisible"><span v-if="formattedDates.length > 0 && !isStageSejour()">
{{ formattedDates }}
</span><span v-else>
{{ formattedDates }}
</span></h2>
        <h3 v-if="offerDetails.offerDetails.titleOffers" v-html="offerDetails.offerDetails.titleOffers"></h3>
        <h3 v-else v-html="offerDetails.presentation"></h3>
         <h2 v-if="!isMenuVisible" style="text-align: right;"> {{ offerDetails.offerDetails.type }} <br v-if="!isMenuVisible">{{ offerDetails.offerDetails.finalCity }} ({{ offerDetails.offerDetails.finalPostcode }})</h2>
     </div>
     <div class="activity-details-description" v-if="!showReservationForm && !paymentSuccess">
      
         <p class="activity-location"><span  v-if="isStageSejour() && formattedDates.length > 1">Du</span ><span v-else>Le</span> {{ formattedDates }} {{ findScheduleByDate(dateSelected)}} : <br v-if="!isMenuVisible"><a :href="'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(offerDetails.offerDetails.finalAddress + ' ' + offerDetails.offerDetails.finalCity + ' ' + offerDetails.offerDetails.finalPostcode)" target="_blank" style="text-decoration: underline;"> <i class="fa fa-map-marker"></i>
 {{ offerDetails.offerDetails.finalAddress }} {{ offerDetails.offerDetails.finalCity }} {{ offerDetails.offerDetails.finalPostcode }}
</a></p>

         <div class="image_activity_left"><img :src="apiImg(offerDetails.images[0].replace('187x97/', '').replace('414x219/', ''))"/></div>
         <br v-if="!isMenuVisible"><br v-if="!isMenuVisible">
         <p class="activity-location">Organisé par Ouicsport</p> <br v-if="!isMenuVisible">
         <p v-html="offerDetails.details" class="p-description-wire"></p>
        <br v-if="!isMenuVisible">
        <iframe v-if="googleDriveLinkFormatted" 
              :src="googleDriveLinkFormatted" 
              width="100%" 
              height="600px" 
              frameborder="0">
        </iframe>
         <section class="tariff-information" v-if="displayPrice">
          <div class="price-tag">
            <h2 v-if="typeof displayPrice === 'string' && displayPrice.includes('-')">
              TARIF : De {{ displayPrice.split('-')[0] }}€ à {{ displayPrice.split('-')[1] }}€
            </h2>
            <h2 v-else>
              TARIF : {{ displayPrice }}€
            </h2>
          </div>
         </section>
     </div>
 
     <div class="reservation-form" v-if="!isStageSejour() && !showReservationForm && !paymentSuccess">
           <div class="form-section date-time">
             <div>
              <label>Date :</label>
               <button type="button" class="btn date-range" >
                   {{ selectedDetail ? formattedDates : 'Sélectionner une date' }}
               </button>
               <div v-if="showModal" class="modal" :data-oc-dismissable="isNative ? '' : null" style="display: block; position: fixed; top: 0; left: 0; width: 100%; height: 100%; background-color: rgba(0, 0, 0, 0.5);">
                 <div class="modal-content">
                       <span class="close" :data-oc-dismiss="isNative ? '' : null" @click="showModal = false">&times;</span>
                       <ul>
                         <li v-for="(detail, index) in futureCriteria" :key="index" @click="selectDetail(detail)">
                          {{ detail.date.split(' ')[0] }} / <span v-for="(criterion, criterionIndex) in detail.criterion" :key="criterionIndex"> {{ criterion.Name }} {{ criterion.Value }} / </span>
                         </li>
                       </ul>
                   </div>
              </div>
             </div>
          <div v-if="selectedDetail && selectedDetail[0].criterion.length > 0">
              <div>
                <label>{{ selectedDetail[0].criterion.map(c => c.Name).join(' / ') }} :</label>
                
                <button 
                  type="button" 
                  class="btn time" 
                  @click="openCriterionModal(selectedDetail)" 
                  :class="{
                    'btn-selected': variations,
                    'btn-grey': !variations,
                    'shake':  showErrorAnimationVariations
                  }"
                >
                  <span v-if="variations" v-html="variations"></span>
                  <span v-else>Sélectionner vos options</span>
                </button>
              </div>

              <div
                v-if="showCriterionModal"
                class="modal"
                :data-oc-dismissable="isNative ? '' : null"
                :style="{
                  display: 'block',
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: selectedDetail[0].quantite == 0 ? 'rgba(128, 128, 128, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                }"
              >
                <div class="modal-content">
                  <span class="close" :data-oc-dismiss="isNative ? '' : null" @click="showCriterionModal = false">&times;</span>
                  
                  <ul>
                    <li 
                      v-for="(option, index) in modalOptions" 
                      :key="index" 
                      :class="{ 'option-disabled': selectedDetail[index].quantite === 0 }"
                      @click="selectedDetail[index].quantite > 0 ? selectCriterionOption(option) : null"
                      :style="{
                        cursor: selectedDetail[index].quantite == 0 ? 'not-allowed' : 'pointer',
                        color: selectedDetail[index].quantite == 0 ? '#ccc' : '#000'
                      }"
                    >
                      <span v-html="option.criterion.map(c => c.Value).join(' / ')"></span>
                      <span v-if="selectedDetail[index].quantite == 0"> (complet)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>






         </div>
           <div class="form-section quantity"  v-if="selectedDetail">
             <div>
             <label>Quantité :</label>
             <div class="quantity-options">
              <div class="flex-input-container">
                <button
                  type="button"
                  @click="quantity = Math.max(quantity - 1, 0); calculateTotal()"
                  class="px-3 py-1 button-selector font-bold"
                  :disabled="quantity <= 0 || (!variations && selectedDetail[0].criterion.length > 0)"
                >
                  –
                </button>

                <input
                  type="number"
                  v-model.number="quantity"
                  @focus="onQuantityFocus"
                  @input="calculateTotal"
                  class="no-arrows quantity-input"
                  min="0"
                  :max="offerDetails.quantite"
                  :disabled="!variations && selectedDetail[0].criterion.length > 0"
                  :class="{ 'shake': !hasSelectedQuantity() && showErrorAnimationQuantity }"
                />

                <button
                  type="button"
                  @click="quantity = Math.min(quantity + 1, offerDetails.quantite); calculateTotal()"
                  class="px-3 py-1 button-selector font-bold"
                  :disabled="quantity >= offerDetails.quantite || (!variations && selectedDetail[0].criterion.length > 0)"
                >
                  +
                </button>
              </div>
               <div class="price">{{ offerDetails.price*quantity }}€</div>
             </div>
           </div>
           </div>
       
           <div class="form-section options" v-if="selectedDetail">
      <div v-if="offerDetails.paidEquipments && offerDetails.paidEquipments.length > 0">
        <label>Option(s) payante(s) :</label>

        <div
          class="quantity-options "
          v-for="equipment in offerDetails.paidEquipments"
          :key="equipment.name"
        >
        <div class="flex-input-container">
          <button
            type="button"
            class="px-3 py-1 button-selector font-bold"
            @click="updateQuantity(equipment.name,
                        Math.max((equipmentQuantities[equipment.name]?.quantity || 0) - 1, 0),
                        equipment.price)"
            :disabled="(equipmentQuantities[equipment.name]?.quantity || 0) <= 0"
          >
            –
          </button>

          <input
            type="number"
            class="no-arrows quantity-input"
            :value="equipmentQuantities[equipment.name]?.quantity || 0"
            @input="updateQuantity(equipment.name, $event.target.value, equipment.price)"
            :min="0"
            :max="quantity"
          />

          <button
            type="button"
            class="px-3 py-1 button-selector font-bold"
            @click="updateQuantity(equipment.name,
                        Math.min((equipmentQuantities[equipment.name]?.quantity || 0) + 1, quantity),
                        equipment.price)"
            :disabled="(equipmentQuantities[equipment.name]?.quantity || 0) >= quantity"
          >
            +
          </button>
        </div>
          

          <p class="option-name">{{ equipment.name }}</p>

          <div class="price">
            {{ (equipmentQuantities[equipment.name]?.quantity || 0) * equipment.price }}€
          </div>
        </div>
      </div>



          </div>


         

             <div class="total-line"></div> <!-- Ajout d'une div pour le trait gris -->
             <div class="total-amount" v-if="selectedDetail">Total : {{ totalPrice.toFixed(2) }}€</div>
       
             <div class="form-section cgv" v-if="selectedDetail">
               <input type="checkbox" id="cgv" name="cgv" v-model="accepteCGV" 
>
               <label for="cgv"  :class="{ 'shake': !accepteCGV && showErrorAnimationCGV }" >J'accepte les conditions générales de vente <!--
                 Natif : navigation interne, l'utilisateur reste dans le tunnel
                 d'achat. Web : lien inchangé.
               --><router-link v-if="isNative" to="/CGU" class="cgv-link">Consulter les CGV OUICSPORT</router-link><a v-else href="/CGU" class="cgv-link">Consulter les CGV OUICSPORT</a></label>
             </div>

             <div class="reservation-button oc-actionbar" v-if="selectedDetail">
              <div v-if="isNative" class="oc-actionbar__price">
                <span class="oc-actionbar__price-label">Total</span>
                <span class="oc-actionbar__price-value">{{ totalPrice.toFixed(2) }}€</span>
              </div>
              <button
                type="button"
                @click="reserver"
                :class="{
                       'btn-selected': (accepteCGV && noCritrion && hasSelectedQuantity()) || (accepteCGV && hasSelectedQuantity() && variations),
                    'btn-grey':   (!accepteCGV || !hasSelectedQuantity() || !noCritrion) && (!accepteCGV || !hasSelectedQuantity() || !variations)
                  }"
              >
                RÉSERVER
              </button>
             </div> 
       
   </div>

   <div class="reservation-form" v-if="isStageSejour() && !showReservationForm && !paymentSuccess">
    <div class="form-section date-time">
             <div>
               <label>Date :</label>
               <button type="button" class="btn date-range" >
                   {{ selectedDetail ? formattedDates : 'Sélectionner une date' }}
               </button>
               <div v-if="showModal" class="modal" :data-oc-dismissable="isNative ? '' : null" style="display: block; position: fixed; top: 0; left: 0; width: 100%; height: 100%; background-color: rgba(0, 0, 0, 0.5);">
                 <div class="modal-content">
                       <span class="close" :data-oc-dismiss="isNative ? '' : null" @click="showModal = false">&times;</span>
                       <ul>
                         <li v-for="(detail, index) in futureCriteria" :key="index" @click="selectDetail(detail)">
                          {{ detail.date.split(' ')[0] }} / <span v-for="(criterion, criterionIndex) in detail.criterion" :key="criterionIndex"> {{ criterion.Name }} {{ criterion.Value }} / </span>
                         </li>
                       </ul>
                   </div>
              </div>

             </div>
             


             <div v-if="selectedDetail && selectedDetail[0].criterion.length > 0">
              <div>
                <label>{{ selectedDetail[0].criterion.map(c => c.Name).join(' / ') }} :</label>
                
                <button 
                  type="button" 
                  class="btn time" 
                  @click="openCriterionModal(selectedDetail)" 
                  :class="{
                      'btn-selected': variations,
                      'btn-grey': !variations,
                      'shake':  showErrorAnimationVariations
                    }"                >
                  <span v-if="variations" v-html="variations"></span>
                  <span v-else>Sélectionner vos options</span>
                </button>
              </div>

              <div
                v-if="showCriterionModal"
                class="modal"
                :data-oc-dismissable="isNative ? '' : null"
                :style="{
                  display: 'block',
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%'
                }"
              >
                <div class="modal-content">
                  <span class="close" :data-oc-dismiss="isNative ? '' : null" @click="showCriterionModal = false">&times;</span>

                  <ul>
                    <li
                      v-for="(option, index) in modalOptions"
                      :key="index"
                      :class="{ 'option-disabled': selectedDetail[index].quantite === 0 }"
                      @click="selectedDetail[index].quantite > 0 ? selectCriterionOption(option) : null"
                      :style="{
                        cursor: selectedDetail[index].quantite == 0 ? 'not-allowed' : 'pointer',
                        color: selectedDetail[index].quantite == 0 ? '#ccc' : '#000'
                      }"
                    >
                      {{ option.criterion.map(c => c.Value).join(' / ') }}   <span v-if="selectedDetail[index].quantite == 0"> complet</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>


           
      
         </div>
         <div class="form-section quantity" v-if="selectedDetail">
  <div>
    <label>Quantité :</label>
    <div class="quantity-options">
      <div class="flex-input-container">
        <button
          type="button"
          @click="quantity = Math.max(quantity - 1, 0); calculateTotal()"
          class="px-3 py-1 button-selector font-bold"
          :disabled="quantity <= 0 || (!variations && selectedDetail[0].criterion.length > 0)"
        >
          –
        </button>

        <input
          type="number"
          v-model.number="quantity"
          @focus="onQuantityFocus"
          @input="calculateTotal"
          class="no-arrows quantity-input"
          min="0"
          :max="offerDetails.quantite"
          :disabled="!variations && selectedDetail[0].criterion.length > 0"
          :class="{ 'shake': !hasSelectedQuantity() && showErrorAnimationQuantity }"
        />

        <button
          type="button"
          @click="quantity = Math.min(quantity + 1, offerDetails.quantite); calculateTotal()"
          class="px-3 py-1 button-selector font-bold"
          :disabled="quantity >= offerDetails.quantite || (!variations && selectedDetail[0].criterion.length > 0)"
        >
          +
        </button>
      </div>

      <div class="price">{{ offerDetails.price * quantity }}€</div>
    </div>
  </div>
</div>

       
           <div class="form-section options" v-if="selectedDetail">

            <div v-if="offerDetails.paidEquipments && offerDetails.paidEquipments.length > 0">
        <label>Option(s) payante(s) :</label>

        <div
          class="quantity-options "
          v-for="equipment in offerDetails.paidEquipments"
          :key="equipment.name"
        >
        <div class="flex-input-container">
          <button
            type="button"
            class="px-3 py-1 button-selector font-bold"
            @click="updateQuantity(equipment.name,
                        Math.max((equipmentQuantities[equipment.name]?.quantity || 0) - 1, 0),
                        equipment.price)"
            :disabled="(equipmentQuantities[equipment.name]?.quantity || 0) <= 0"
          >
            –
          </button>

          <input
            type="number"
            class="no-arrows quantity-input"
            :value="equipmentQuantities[equipment.name]?.quantity || 0"
            @input="updateQuantity(equipment.name, $event.target.value, equipment.price)"
            :min="0"
            :max="quantity"
          />

          <button
            type="button"
            class="px-3 py-1 button-selector font-bold"
            @click="updateQuantity(equipment.name,
                        Math.min((equipmentQuantities[equipment.name]?.quantity || 0) + 1, quantity),
                        equipment.price)"
            :disabled="(equipmentQuantities[equipment.name]?.quantity || 0) >= quantity"
          >
            +
          </button>
        </div>
          

          <p class="option-name">{{ equipment.name }}</p>

          <div class="price">
            {{ (equipmentQuantities[equipment.name]?.quantity || 0) * equipment.price }}€
          </div>
        </div>
      </div>

</div>
         <!-- Option Assurance Chapka (uniquement pour stages/séjours) -->
         <div class="form-section insurance" v-if="selectedDetail && quantity > 0 && insuranceDates.depart">
           <ChapkaInsurance
             :offer-type="offerDetails.offerDetails.type"
             :offer-id="offerDetails.offerId"
             :participant-count="quantity"
             :start-date="insuranceDates.depart"
             :end-date="insuranceDates.retour"
             :total-amount="offerDetails.price * quantity"
             destination="FR"
             @update:insurance="handleInsuranceUpdate"
             @update:participants="handleParticipantsUpdate"
             @quote-loaded="handleQuoteLoaded"
           />
         </div>

             <div class="total-line"></div> 
             <div class="total-amount" v-if="selectedDetail">Total : {{ totalPrice.toFixed(2) }}€</div>
             <div class="insurance-included" v-if="selectedDetail && insuranceAccepted && insurancePremium > 0">
               <span class="insurance-label-total">dont assurance Cap Explorer : {{ insurancePremium.toFixed(2) }}€</span>
             </div>
       
             <div class="form-section cgv" v-if="selectedDetail">
               <input type="checkbox" id="cgv" name="cgv" v-model="accepteCGV"  
>
               <label for="cgv"  :class="{ 'shake': !accepteCGV && showErrorAnimationCGV }">J'accepte les conditions générales de vente <!--
                 Natif : le lien absolu vers www.ouicsport.fr éjectait
                 l'utilisateur hors de l'application en plein paiement.
                 Web : lien inchangé.
               --><router-link v-if="isNative" to="/CGU" class="cgv-link">Consulter les CGV OUICSPORT</router-link><a v-else href="https://www.ouicsport.fr/admin-website/cgu" class="cgv-link">Consulter les CGV OUICSPORT</a></label>
             </div>

             <div class="reservation-button oc-actionbar" v-if="selectedDetail">
              <div v-if="isNative" class="oc-actionbar__price">
                <span class="oc-actionbar__price-label">Total</span>
                <span class="oc-actionbar__price-value">{{ totalPrice.toFixed(2) }}€</span>
              </div>
              <button
                type="button"
                @click="reserver"
                :class="{
                    'btn-selected': (accepteCGV && noCritrion && hasSelectedQuantity()) || (accepteCGV && hasSelectedQuantity() && variations),
                    'btn-grey':   (!accepteCGV || !hasSelectedQuantity() || !noCritrion) && (!accepteCGV || !hasSelectedQuantity() || !variations)
                  }"
              >
                RÉSERVER
              </button>
             </div> 
  </div>
   
  <p v-if="showReservationForm && !paymentSuccess" style="    padding-top: 25px;
    padding-left: 20px;
" class="activity-location">Du {{ formattedDates }}  {{ findScheduleByDate(dateSelected)}} : <br v-if="!isMenuVisible"><a :href="'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(offerDetails.offerDetails.finalAddress + ' ' + offerDetails.offerDetails.finalCity + ' ' + offerDetails.offerDetails.finalPostcode)" target="_blank" style="text-decoration: underline;"> <i class="fa fa-map-marker"></i>
 {{ offerDetails.offerDetails.finalAddress }} {{ offerDetails.offerDetails.finalCity }} {{ offerDetails.offerDetails.finalPostcode }}
</a></p>
  <div class="payment-form" v-if="showReservationForm && !paymentSuccess" >
    <div class="form-section details">
      <h2 style="margin-bottom: 20px;">Détails</h2>
      <div class="details-row" >
        <input type="number" class="btn quantity-input" v-model="quantity" disabled><input v-if="!noCritrion" type="text" class="btn quantity-input" v-model="displayVariations" disabled :style="{ width: !isMenuVisible ? 'auto' : '80%' }"><div class="price">{{ offerDetails.price * quantity }}€</div>
      </div>

      <div class="details-row" v-for="(equipment, index) in equipmentQuantities" :key="index" >
        <input type="number" class="btn quantity-input" v-model="equipment.quantity" disabled><input type="text" class="btn quantity-input" :value="index" disabled :style="{ width: !isMenuVisible ? 'auto' : '80%' }"><div class="price">{{ equipment.price * equipment.quantity }}€</div>
      </div>
      
      <!-- Ligne assurance Chapka -->
      <div class="details-row insurance-recap" v-if="insuranceAccepted && insurancePremium > 0">
        <div class="insurance-recap-label">
          <span class="shield-icon">🛡️</span>
          <span class="insurance-name">Assurance Cap Explorer</span>
          <span class="insurance-tag">Inclus</span>
        </div>
        <div class="price insurance-price">{{ insurancePremium.toFixed(2) }}€</div>
      </div>
      
        <div class="divider"></div>
      <div class="total" style="position: relative; margin:auto;">
        <div class="total-amount">Total : {{ totalPrice.toFixed(2) }}€</div>
      </div>
    </div>

    <div class="form-section promo-code">
      <input
        type="text"
        class="btn promo-input"
        placeholder="CODE PROMO"
        v-model="promoCode"
        :disabled="!isLoggedIn"
      >
      <button
        type="button"
        class="validate"
        :disabled="!isLoggedIn"
        @click="voucherActivateFront(promoCode)"
      >
        VALIDER
      </button>
    </div>
    <p v-if="!isLoggedIn" class="promo-login-hint">
      <router-link :to="{ name: 'LoginPage' }">Connectez-vous pour utiliser un code promo</router-link>
    </p>
    <p v-if="voucherError" class="error-text">{{ voucherError }}</p>
    <p v-if="voucherMessage" class="success-text">{{voucherMessage}}</p>

    <div class="form-section credits">
      <h3>PAYER AVEC MES CRÉDITS</h3>
      <div class="credits-info">Solde disponible : {{ creditsBalance }}€</div>
      <button 
        type="button" 
        class="btn use-credits" 
        :disabled="creditsBalance <= 0 || creditsToUse > 0"
        @click="useCredits"
      >
        UTILISER
      </button>
    </div>
    


    <div class="form-section card-payment">
      <h3>PAYER AVEC MA CB</h3>
      <div class="remaining-amount">MONTANT RESTANT À PAYER : {{ remainingAmount.toFixed(2) }}€</div>
    </div>
    <p class="error-message" v-if="paymentError">{{ paymentError }}</p>
    <div v-if="showReservationForm && !paymentSuccess" class="reservation-section">
      <div id="card-element" class="stripe-element" v-if="!isKlarnaSelected">
      </div>
      <div class="klarna-message" v-else>
        <p>
          En sélectionnant « Payer », vous serez redirigé vers Klarna pour finaliser votre paiement en toute sécurité.
        </p>
      </div>
    </div>


    <div class="form-section installment">

      <!--
        Klarna est masqué dans l'application native (et uniquement là : sur le
        web, `isNative` vaut false et l'affichage est inchangé).
        confirmKlarnaPayment redirige vers le domaine Klarna en passant
        `return_url: window.location.href`, qui vaut `https://localhost/...`
        dans la webview Capacitor. Cette adresse n'existe pas en dehors de
        l'app : la redirection sort de l'application et le retour de paiement
        n'aboutit jamais. Tant que le tunnel Klarna n'est pas repris avec une
        URL de retour publique et un schéma de redirection natif, le paiement
        en 3 fois ne doit pas être proposé sur mobile.
      -->
      <button class="stripe-button" @click="togglePaymentMethod" v-if="isKlarnaSelected && !isNative">Paiement en 1 fois.</button>
      <div v-if="isStageSejour() && !paymentSuccess && !isNative" class="KlarnaElement"><div class="installment-info" v-if="!isKlarnaSelected">Paiement en 3 fois sans frais avec</div>
      <button  class="klarna-button" @click="togglePaymentMethod" v-if="!isKlarnaSelected ">Klarna.</button>

      </div>

    </div>

    <div class="form-section payment-button oc-actionbar">
      <div v-if="isNative" class="oc-actionbar__price">
        <span class="oc-actionbar__price-label">À payer</span>
        <span class="oc-actionbar__price-value">{{ remainingAmount.toFixed(2) }}€</span>
      </div>
      <button type="submit" class="btn pay" @click="submitPayment" :disabled="loadingPayment">
        {{ loadingPayment ? 'PAIEMENT EN COURS...' : 'PAYER' }}
      </button>
    </div>
  </div>



 </div>
  </template>
</div>


</template>
<style scoped>
.modal-content {
 background-color: #fff; /* Couleur de fond */
 padding: 20px; /* Espace intérieur */
 border-radius: 8px; /* Bords arrondis */
 box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2); /* Ombre portée */
 width: 80%; /* Largeur du modal */
 max-width: 600px; /* Largeur maximale */
 max-height: 70vh; /* Hauteur maximale */
 overflow-y: auto;
}

.btn-grey {
  background-color: #808080; /* Gris clair */
  color: #d3d3d3; /* Texte gris */
  border: 1px solid #ccc; /* Bordure grise */
}

.close {
 color: #aaa; /* Couleur du bouton de fermeture */
 float: right; /* Positionnement à droite */
 font-size: 28px; /* Taille de la police */
 cursor: pointer; /* Curseur pointeur */
}

.close:hover {
 color: #333; /* Couleur au survol */
}

.modal-content ul {
 list-style: none; /* Suppression des puces */
 padding: 0; /* Suppression du padding */
 margin: 0; /* Suppression de la marge */
 margin-bottom: 20px; 
}

.modal-content li {
 padding: 10px; /* Espace intérieur des éléments de liste */
 border-bottom: 1px solid #eee; /* Bordure inférieure */
 cursor: pointer; /* Curseur pointeur */
}

.modal-content li:hover {
 background-color: #f8f8f8; /* Couleur de fond au survol */
}

/* Styles pour les barres de défilement (optionnel) */
.modal-content::-webkit-scrollbar {
 width: 10px; /* Largeur de la barre de défilement */
}

.modal-content::-webkit-scrollbar-track {
 background: #f1f1f1; /* Couleur de fond de la piste */
}

.modal-content::-webkit-scrollbar-thumb {
 background: #888; /* Couleur de la barre de défilement */
}

.modal-content::-webkit-scrollbar-thumb:hover {
 background: #555; /* Couleur de la barre de défilement au survol */
}

/* Adaptation pour les petits écrans */
@media (max-width: 600px) {
 .modal-content {
     width: 95%; /* Largeur plus grande pour les petits écrans */
     margin: 5% auto; /* Marge verticale réduite */
 }
}

/* Styles pour le bouton "Réserver" */
.reservation-button button {
  color: #fff; /* Couleur du texte par défaut */
  padding: 10px 20px;
  border: none;
  transition: background-color 0.3s ease; /* Transition de la couleur de fond */
}

/* Styles pour le bouton actif (lorsque accepteCGV est vrai) */
.reservation-button button:not(:disabled) {
  cursor: pointer; 
}

/* Styles pour l'animation de transition */
.reservation-button button:hover:not(:disabled) {
  transform: scale(1.05);
  transition: background-color 0.3s ease, transform 0.2s ease; 
}

.error-text {
  color: #ff4d4d;
  font-size: 0.9rem;
  margin-top: 5px;
}

.promo-login-hint {
  font-size: 0.9rem;
  margin-top: 5px;
  text-align: center;
}

.promo-login-hint a {
  color: #0066cc;
  text-decoration: underline;
}

/* Styles pour l'assurance Chapka */
.form-section.insurance {
  margin: 20px 0;
  padding: 0 15px;
}

.insurance-included {
  text-align: center;
  margin-top: 5px;
  padding-bottom: 10px;
}

.insurance-label-total {
  color: #c0392b;
  font-size: 0.9rem;
  font-style: italic;
}

/* Nouveau style pour le récap assurance */
.details-row.insurance-recap {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(231, 76, 60, 0.08); /* Fond rouge très léger pour rappeler Chapka */
  border: 1px solid rgba(231, 76, 60, 0.2);
  border-radius: 12px;
  padding: 12px 15px;
  margin-top: 15px;
  margin-bottom: 15px;
}

.insurance-recap-label {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.shield-icon {
  font-size: 1.2rem;
}

.insurance-name {
  font-family: 'Gagalin', sans-serif;
  color: #c0392b;
  font-size: 1rem;
  letter-spacing: 0.5px;
}

.insurance-tag {
  background: #27ae60;
  color: white;
  font-size: 0.65rem;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  font-weight: bold;
  font-family: 'Dosis', sans-serif;
  letter-spacing: 1px;
}

.details-row .insurance-price {
  font-family: 'Gagalin', sans-serif;
  color: #c0392b;
  font-size: 1.2rem;
  font-weight: normal;
}

/* Responsive pour le tag */
@media (max-width: 400px) {
  .insurance-tag {
    display: none;
  }
  .insurance-name {
    font-size: 0.9rem;
  }
}

/* Styles pour le bouton de paiement */
.btn.pay:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.error-message {
  color: #e85746;
  font-size: 0.95rem;
  margin: 10px 0;
  padding: 10px;
  background-color: #ffe6e6;
  border-left: 4px solid #e85746;
  border-radius: 4px;
}

.stripe-element {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-bottom: 15px;
}

</style>
<script>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { useStore, } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import ChapkaInsurance from '@/components/ChapkaInsurance.vue';
import { notifyInsurance, extractDatesFromCriteria } from '@/api/chapka';
import { apiImg, sportBanner, isNative } from '@/api/base';
import { shareOffer } from '@/native/share';



export default {
 name: 'OfferDetails',
 props: ['offerId'],
 components: {
     ChapkaInsurance
 },
 
 data() {
     return {
        showCriterionModal: false,
       showKlarnaSoon: false,
        modalOptions: [],
       showModal: false,
       currentCriterion: null,
       variations: "",
       equipmentQuantities: {},
       accepteCGV: false,
       animating: false,
       showReservationForm: false,
       selectedCriteria: [],
       showErrorAnimation: false,
       showErrorAnimationCGV: false,
        showErrorAnimationQuantity: false,
        showErrorAnimationVariations: false,
        isMenuVisible: false,
        promoCode: '',
     };
 },
 computed: {
  /* ---------------------------------------------------------------
     Écran natif. Ces propriétés ne servent qu'à l'application : le
     balisage du site ne les référence pas.
     --------------------------------------------------------------- */

  /** Raccourci vers le sous-objet le plus utilisé de la réponse. */
  rvOd() {
    return (this.offerDetails && this.offerDetails.offerDetails) || {};
  },

  rvTitre() {
    return this.rvOd.titleOffers || (this.offerDetails && this.offerDetails.presentation) || '';
  },

  rvAdresse() {
    const o = this.rvOd;
    const brut = [o.finalAddress, o.finalCity, o.finalPostcode].filter(Boolean).join(' ');
    // Le back renvoie les apostrophes encodées (« l&#039;École ») : affichées
    // telles quelles dans une interpolation, elles restent lisibles à l'écran.
    const zone = document.createElement('textarea');
    zone.innerHTML = brut;
    return zone.value;
  },

  /** Retour vers la fiche, en conservant le paramètre composite reçu. */
  rvRetourFiche() {
    return { name: 'OfferDetailsPage', params: { id: this.offerId } };
  },

  /**
   * Nom du critère à choisir : « Niveaux », « Horaire »… Il est composé
   * côté serveur et varie d'une offre à l'autre. Vide quand l'offre n'a
   * aucun critère (cas du canoë), auquel cas la section n'est pas rendue.
   */
  rvCritereNom() {
    const d = this.selectedDetail;
    if (!d || !d[0] || !Array.isArray(d[0].criterion) || !d[0].criterion.length) return '';
    return d[0].criterion.map((c) => c.Name).join(' / ');
  },

  /** La quantité reste bloquée tant que le critère n'est pas choisi. */
  rvVerrouParCritere() {
    return !this.variations && !!this.rvCritereNom;
  },

  rvOptions() {
    return (this.offerDetails && this.offerDetails.paidEquipments) || [];
  },

  /**
   * Places réellement disponibles sur un créneau.
   *
   * L'API renvoie un stock négatif lorsqu'un créneau a été surréservé
   * (« -4 » sur le stage de Sologne) : ce n'est pas une disponibilité, c'est
   * un dépassement. Toute comparaison à zéro strict le laissait passer.
   */
  rvPlaces() {
    return (c) => {
      const n = parseInt(c && c.quantite, 10);
      return Number.isFinite(n) && n > 0 ? n : 0;
    };
  },

  /** Stock de l'offre, jamais négatif — sert de borne au sélecteur. */
  rvStock() {
    const n = parseInt(this.offerDetails && this.offerDetails.quantite, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  },

  /**
   * Montants à la française. L'API renvoie « 289.00 » et les calculs
   * produisent des points décimaux : sans cela l'écran mêle « 289.00 € »
   * et « 54,00 € » sur la même page.
   */
  rvEuro() {
    return (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n.toFixed(2).replace('.', ',') : v;
    };
  },

  /**
   * Conditions d'ouverture du paiement. On reprend exactement celles que
   * le site applique à son bouton : CGV acceptées, quantité positive, et
   * critère choisi lorsque l'offre en impose un.
   */
  rvPeutReserver() {
    if (!this.accepteCGV || !this.quantity || this.quantity <= 0) return false;
    return Boolean(this.noCritrion || this.variations);
  },

  displayVariations() {
      const txt = document.createElement('textarea');
      txt.innerHTML = this.variations;
      return txt.value;
    },
   futureCriteria() {
    
    if (this.offerDetails && this.offerDetails.offerDetails.type === "Stage/Séjour") {

      const earliestDateStr = this.findEarliestFutureDate(this.offerDetails.criteria.map(detail => detail.date));
      return this.offerDetails.criteria.filter(detail => detail.date.startsWith(earliestDateStr));
    } else {
      return this.offerDetails.criteria.filter(detail => this.isAfterToday(detail.date));
    }
},
useFirstCriterionMax() {
    return (
      this.selectedDetail &&
      Array.isArray(this.selectedDetail) &&
      this.selectedDetail[0] &&
      this.selectedDetail[0].criterion &&
      this.selectedDetail[0].criterion.length > 0
    );
  },

  

 },


 mounted() {
  this.observeReservationForm();
  this.checkScreenWidth();
  window.addEventListener('resize', this.checkScreenWidth);
  
  },
 setup(props) {
    const route  = useRoute();
    const router = useRouter();
     const offerDetails = ref(null);
     const loading = ref(true);
     const loadingPayment = ref(false);
     const error = ref(false);
     const store = useStore();
     const currentMonth = ref(new Date().getMonth());
     const currentYear = ref(new Date().getFullYear());
     const isLoggedIn = computed(() => store.state.auth.user !== null);
     const stripe = ref(null);
      const cardElement = ref(null);
      const quantity = ref(0);
      const selectedDetail = ref(null);
      const clientSecret = ref(null);
      const publicKey = ref(null);
      const paymentSuccess = ref(false); 
      const paymentDetails = ref(null);
      const paymentError = ref(null);
      const offerID = ref(null);
      const formattedDates = ref([]);
      const dateSelected = ref(null);
      const equipementJSON = ref(null);
      const googleDriveLinkFormatted = ref(null);
      const isKlarnaSelected = ref(false);
      const creditsBalance = ref(0);
      const creditsToUse = ref(0);
      const voucher = ref(0);
      const voucherId = ref(0);
      const voucherError = ref(''); 
      const totalPrice = ref(0);
      const voucherMessage = ref('');
      const noCritrion = ref(false);
      
      // Variables pour l'assurance Chapka
      const insuranceAccepted = ref(false);
      const insurancePremium = ref(0);
      const insuranceFormule = ref('AN');
      const insuranceProduit = ref('CAP-EXPLORER');
      const insuranceQuote = ref(null);
      const insuranceParticipants = ref([]);
      const insuranceParticipantsValid = ref(false);
      const insuranceDates = ref({ depart: null, retour: null });
      
      const remainingAmount = computed(() => {
        const total = Number(totalPrice.value || 0);
        const used  = Number(creditsToUse.value || 0);
        return Math.max(0, total - used);
      });
      

      function findEarliestFutureDate(dates) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const futureDates = dates.filter(date => {
              const dateParts = date.split(' ')[0].split('/');
              const dateObj = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
              return dateObj > today;
          });
          if (futureDates.length === 0) return null;
          return futureDates.reduce((earliest, date) => {
              const earliestDateParts = earliest.split(' ')[0].split('/');
              const earliestDate = new Date(earliestDateParts[2], earliestDateParts[1] - 1, earliestDateParts[0]);
              const currentDateParts = date.split(' ')[0].split('/');
              const currentDate = new Date(currentDateParts[2], currentDateParts[1] - 1, currentDateParts[0]);
              return currentDate < earliestDate ? date : earliest;
          });
      }




      function futureCriteriainit(specificDate) {
        
        if (!offerDetails.value || !offerDetails.value.criteria) {
            return [];
        }

        if (offerDetails.value.offerDetails && offerDetails.value.offerDetails.type === "Stage/Séjour") {
            const earliestDateStr = findEarliestFutureDate(offerDetails.value.criteria.map(detail => detail.date));
            specificDate = earliestDateStr ? earliestDateStr.split(' ')[0] : specificDate;
        }

        const filteredCriteria = offerDetails.value.criteria.filter(detail => detail.date.startsWith(specificDate));
        if (filteredCriteria.length === 0) {
            return [];
        }

        const combinedCriteria = [];

        filteredCriteria.forEach(detail => {
            const groupedCriteria = {
                date: detail.date,
                price: detail.price,
                quantite: detail.quantite,
                criterion: [] 
            };

            detail.criterion.forEach(criterion => {
                groupedCriteria.criterion.push({
                    offerId: criterion.offerId,
                    id: criterion.id,
                    Name: criterion.Name,
                    Value: criterion.Value
                });
            });

            combinedCriteria.push(groupedCriteria);
        });

        return combinedCriteria;
    }






onMounted(async () => {
    const redirectStatus = route.query.redirect_status;
    const paymentIntentParam = route.query.payment_intent;

    // Retour de Klarna : traiter le résultat du paiement
    if (redirectStatus && paymentIntentParam) {
      if (redirectStatus === 'succeeded') {
        // loading reste true (spinner visible) pendant le traitement
        try {
          await processPayment(paymentIntentParam);
          // processPayment redirige vers ConfirmationPage en cas de succès
        } catch (err) {
          console.error('[Klarna] Erreur processPayment après redirect:', err);
          paymentError.value = 'Échec de la confirmation du paiement. Veuillez contacter le support.';
          loading.value = false;
          error.value = true;
        }
      } else {
        // redirect_status = 'failed' ou autre
        paymentError.value = 'Le paiement Klarna a échoué ou a été annulé. Veuillez réessayer.';
        loading.value = false;
        error.value = true;
      }
      return;
    }

    try {
        var offerIDS = props.offerId;
        var parts = offerIDS.split(' ');

        var id = parts[0];
        offerID.value = id;
        // Gérer le cas où ID_Criteria n'a que la date sans l'heure (dates groupées)
        var date_criteria = parts.length >= 3 ? parts[1] + ' ' + parts[2] : parts[1];
        const response = await axios.get(`https://www.ouicsport.fr/api/search/offer-details-json/${offerID.value}`);
        offerDetails.value = JSON.parse(response.data["0"]);
        // Chercher par match exact d'abord, puis par startsWith pour les dates groupées (sans heure)
        const crit = offerDetails.value.criteria.find(c => c.date === date_criteria)
                  || offerDetails.value.criteria.find(c => c.date.startsWith(date_criteria + ' '));

        if (crit && Array.isArray(crit.criterion) && crit.criterion.length === 0) {
          noCritrion.value = true;
          offerDetails.value.price         = crit.price;
          offerDetails.value.quantite      = crit.quantite;
          offerDetails.value.criterion     = crit.criterion;
          offerDetails.value.selected_date = crit.date;
        }
        

        if (offerDetails.value && offerDetails.value.dates) {
            const [year, month, ] = offerDetails.value.dates.split(',')[0].split('-');
            currentYear.value = parseInt(year, 10);
            currentMonth.value = parseInt(month, 10) - 1; 
        }

        if (offerDetails.value.offerDetails && offerDetails.value.offerDetails.googleDriveLink) {
          const link = offerDetails.value.offerDetails.googleDriveLink;
          if (link.includes('drive.google.com/file/d/')) {
            googleDriveLinkFormatted.value = link.replace('/view?usp=sharing', '/preview');
          }
        }

        if (offerDetails.value.offerDetails && (offerDetails.value.offerDetails.type === "Stage à la carte" || offerDetails.value.offerDetails.type === 'Séjour' || offerDetails.value.offerDetails.type === 'Stage' || offerDetails.value.offerDetails.type === 'Stage/Séjour') ) {

          
            const criteriaDates = offerDetails.value.criteria.map(c => new Date(c.date.split(' ')[0].split('/').reverse().join('-')));
            const sortedDates = criteriaDates.sort((a, b) => a - b);
            const firstDate = sortedDates[0];
            const lastDate = sortedDates[sortedDates.length - 1];


            if(offerDetails.value.offerDetails.type != "Stage à la carte" || offerDetails.value.offerDetails.type != "Séjour"){
              if (firstDate.getTime() === lastDate.getTime()) {
                formattedDates.value = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(firstDate);
            } else {
                formattedDates.value = `${new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(firstDate)} au ${new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(lastDate)}`;
            }
            }else{
              if (firstDate.getTime() === lastDate.getTime()) {
                formattedDates.value = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(firstDate);
            } else {
                formattedDates.value = `${new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(firstDate)} au ${new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(lastDate)}`;
            }
            }

        } else {
            const [day, month, year] = date_criteria.split(' ')[0].split('/');
            const dateObj = new Date(year, month - 1, day);
            formattedDates.value = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(dateObj);
        }

        localStorage.setItem('formattedDates', formattedDates.value);

        selectedDetail.value = futureCriteriainit(date_criteria);
        dateSelected.value = date_criteria;
        
        // Calculer les dates pour l'assurance Chapka
        computeInsuranceDates();
    } catch (err) {
        console.error("Erreur lors de la récupération des détails de l'offre:", err);
        error.value = true;
    } finally {
        loading.value = false;
    }
});



function decodeHtmlEntities(html) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  return doc.documentElement.textContent
}

   
     
      const setupStripe = (pk_key) => {
        stripe.value = window.Stripe(pk_key);
        const elements = stripe.value.elements();
        var style = {
          base: {
            color: "#32325d",
            fontWeight: 500,
            fontFamily: "Source Sans Pro, Consolas, Menlo, monospace",
            fontSize: "16px",
            fontSmoothing: "antialiased",

            "::placeholder": {
              color: "#CFD7DF"
            },
            ":-webkit-autofill": {
              color: "#e39f48"
            }
          },
          invalid: {
            iconColor: "#e85746",
            color: "#e85746"
          }
        };

        cardElement.value = elements.create('card', { style: style });

        // Vérifier que l'élément DOM existe
        const cardElementDom = document.getElementById('card-element');
        if (!cardElementDom) {
          paymentError.value = 'Erreur lors de l\'initialisation du formulaire de paiement.';
          return;
        }

        cardElement.value.mount('#card-element');

        // Écouter les erreurs de validation
        cardElement.value.on('change', (event) => {
          if (event.error) {
            paymentError.value = event.error.message;
          } else {
            paymentError.value = null;
          }
        });
      };

      

      const submitPayment = async () => {
        loadingPayment.value = true;
        paymentError.value = null; // Réinitialiser les erreurs

        if (!stripe.value || !clientSecret.value) {
          paymentError.value = "Erreur d'initialisation du paiement. Veuillez réessayer.";
          loadingPayment.value = false;
          return;
        }

        // Garde-fou : même si un état résiduel laissait Klarna sélectionné dans
        // l'app native, on ne lance pas une redirection dont le retour est
        // impossible. On bascule silencieusement sur le paiement par carte.
        if (isNative && isKlarnaSelected.value) {
          isKlarnaSelected.value = false;
        }

        try {
          if (isKlarnaSelected.value) {
            const result = await stripe.value.confirmKlarnaPayment(clientSecret.value, {
              payment_method: {
                billing_details: {
                  email: store.state.auth.user.data.email,
                  address: {
                    country: 'FR',
                  },
                },
              },
              return_url: window.location.href
            });

            if (result.error) {
              paymentError.value = result.error.message;
              loadingPayment.value = false;
            }
          } else {
            const result = await stripe.value.confirmCardPayment(clientSecret.value, {
              payment_method: {
                card: cardElement.value,
              },
            });

            if (result.error) {
              paymentError.value = result.error.message;
              loadingPayment.value = false;
            } else if (result.paymentIntent.status === 'succeeded') {
              await processPayment(result.paymentIntent.id);
              loadingPayment.value = false;
            } else {
              paymentError.value = `Statut de paiement inattendu: ${result.paymentIntent.status}`;
              loadingPayment.value = false;
            }
          }
        } catch (error) {
          paymentError.value = 'Une erreur inattendue est survenue. Veuillez réessayer.';
          loadingPayment.value = false;
        }
      };


const SetupEquipementJSON = async (equipmentQuantities) => { 
  const formatEquipmentQuantities = equipment => {
    return Object.entries(equipment).map(([name, details]) => ({
      name: name,
      quantity: parseInt(details.quantity),
      price: parseFloat(details.price) 
    }));
  };
  equipementJSON.value = JSON.stringify(formatEquipmentQuantities(equipmentQuantities));
};

const createPaymentIntent = async (equipmentQuantities) => {
  try {
    const formatEquipmentQuantities = equipment => {
        return Object.entries(equipment).map(([name, details]) => ({
          name: name,
          quantity: parseInt(details.quantity),
          price: parseFloat(details.price) 
        }));
      };
    let dateParts = offerDetails.value.selected_date.split(" ")[0].split("/");

    let formattedDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;

    let formData = new FormData();
    formData.append('userId', store.state.auth.user.data.id);
    formData.append('quantity', quantity.value);
    formData.append('date', formattedDate);
    formData.append('dateFull', localStorage.getItem('formattedDates'));
    formData.append('variations', JSON.stringify(offerDetails.value.criterion.map(option => decodeHtmlEntities(option.Value))));
    equipmentQuantities = equipmentQuantities && Object.keys(equipmentQuantities).length === 0 ? null : equipmentQuantities;
    if (equipmentQuantities != null) {
      formData.append('selectedEquipments', JSON.stringify(formatEquipmentQuantities(equipmentQuantities)));
      equipementJSON.value =  JSON.stringify(formatEquipmentQuantities(equipmentQuantities));
    }    
    if (voucher.value > 0) {
      formData.append('voucherId', voucherId.value);
    }
    if (creditsToUse.value > 0) {
      formData.append('credits', creditsToUse.value);
    }

    const paymentEndpoint = isKlarnaSelected.value
          ? `https://www.ouicsport.fr/api/search/process-payment-Klarna/${offerID.value}`
          : `https://www.ouicsport.fr/api/search/process-payment/${offerID.value}`;

    const response = await axios.post(paymentEndpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    clientSecret.value = response.data.data.pi_client_secret;
    publicKey.value = response.data.data.pk_key;

    // Stocker les données d'assurance Chapka
    if (insuranceAccepted.value && insuranceQuote.value) {
      localStorage.setItem('chapkaInsurance', JSON.stringify({
        accepted: true,
        premium: insurancePremium.value,
        formule: insuranceFormule.value,
        produit: insuranceProduit.value,
        totalAmount: offerDetails.value.price * quantity.value,
        quote: insuranceQuote.value,
        participants: insuranceParticipants.value,
        dates: insuranceDates.value
      }));
    } else {
      localStorage.removeItem('chapkaInsurance');
    }

    if (!isKlarnaSelected.value) {
      localStorage.setItem('isKlarnaSelected', isKlarnaSelected.value);
      setupStripe(publicKey.value);
      const cardEl = document.getElementById('card-element');
      if (cardEl) cardEl.style.display = 'block';
    } else {
      localStorage.setItem('isKlarnaSelected', isKlarnaSelected.value);
      localStorage.setItem('offerDetails', JSON.stringify(offerDetails.value));
      localStorage.setItem('quantity', quantity.value);
      localStorage.setItem('variation', JSON.stringify(offerDetails.value.criterion.map(option => option.Value)));
      if (equipementJSON.value) {
        localStorage.setItem('equipmentQuantities', equipementJSON.value);
      } else {
        localStorage.removeItem('equipmentQuantities');
      }
      localStorage.setItem('creditsToUse', String(creditsToUse.value || 0));
      localStorage.setItem('klarnaOfferID', offerID.value);
      if (voucher.value > 0) {
        localStorage.setItem('klarnaVoucher', String(voucher.value));
        localStorage.setItem('klarnaVoucherId', String(voucherId.value));
      } else {
        localStorage.removeItem('klarnaVoucher');
        localStorage.removeItem('klarnaVoucherId');
      }
      const cardEl = document.getElementById('card-element');
      if (cardEl) cardEl.style.display = 'none';
    }
    

  } catch (error) {
    console.error('Error creating payment intent:', error);
  }
};


    // const processPayment = async (paymentIntentId) => {
    //     let dateParts = offerDetails.value.selected_date.split(" ")[0].split("/");

    //     let formattedDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;

    //     // Créez une instance de FormData
    //     let formData = new FormData();

    //     formData.append('userId', store.state.auth.user.data.id);
    //     formData.append('quantity', quantity.value);
    //     formData.append('date', formattedDate);
    //     formData.append('variations', JSON.stringify(offerDetails.value.criterion.map(option => decodeHtmlEntities(option.Value))));
    //     formData.append('paymentIntentId', paymentIntentId);
    //     if (equipementJSON.value != null) {
    //       formData.append('selectedEquipments', equipementJSON.value);
    //     }    
    //     if (voucher.value && voucher.value > 0) {
    //       formData.append('voucherId',voucherId.value);
    //     }
    //     const paymentEndpoint = isKlarnaSelected.value
    //       ? `https://www.ouicsport.fr/api/search/process-payment-Klarna/${offerID.value}`
    //       : `https://www.ouicsport.fr/api/search/process-payment/${offerID.value}`;

    //       if (isKlarnaSelected.value) {
    //         localStorage.setItem('offerDetails', JSON.stringify(offerDetails.value));
    //         localStorage.setItem('quantity', quantity.value);
    //         localStorage.setItem('variation', JSON.stringify(offerDetails.value.criterion.map(option => option.Value)));
    //         localStorage.setItem('equipmentQuantities',  equipementJSON.value);
    //       }
    //     const response = await axios.post(paymentEndpoint, formData, {
    //       headers: {
    //         'Content-Type': 'multipart/form-data'
    //       }
    //     });

    //     paymentSuccess.value = true;
    //     paymentDetails.value = response.data.data;

        
    //     localStorage.setItem('offerDetails', JSON.stringify(offerDetails.value));
    //     localStorage.setItem('quantity', quantity.value);
    //     localStorage.setItem('variation', JSON.stringify(offerDetails.value.criterion.map(option => option.Value)));
    //     localStorage.setItem('equipmentQuantities',  equipementJSON.value);
    //     router.push({ name: 'ConfirmationPage' });
    //     };


    const processPayment = async (paymentIntentId) => {
      let offerData, qty, variationsArray, equipmentsJSON;
      const stored = localStorage.getItem('isKlarnaSelected');
      isKlarnaSelected.value = (stored === 'true');      
      if (isKlarnaSelected.value && localStorage.getItem('offerDetails')) {
        offerData        = JSON.parse(localStorage.getItem('offerDetails'));
        qty              = Number(localStorage.getItem('quantity'));
        variationsArray  = JSON.parse(localStorage.getItem('variation'));
        equipmentsJSON   = localStorage.getItem('equipmentQuantities');
      } else {
        offerData        = offerDetails.value;
        qty              = quantity.value;
        variationsArray  = offerDetails.value.criterion.map(o => decodeHtmlEntities(o.Value));
        equipmentsJSON   = equipementJSON.value;
      }

      const dateParts = offerData.selected_date.split(" ")[0].split("/");
      const formattedDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;

      const formData = new FormData();
      formData.append('userId', store.state.auth.user.data.id);
      formData.append('quantity', qty);
      formData.append('date', formattedDate);
      formData.append('variations', JSON.stringify(variationsArray));
      formData.append('dateFull', localStorage.getItem('formattedDates'));
      formData.append('paymentIntentId', paymentIntentId);


      if (equipmentsJSON && equipmentsJSON !== 'null' && equipmentsJSON !== 'undefined') {
        formData.append('selectedEquipments', equipmentsJSON);
      }
      // Restaurer le voucher depuis localStorage pour Klarna
      let voucherIdForFinalize = voucherId.value;
      if (isKlarnaSelected.value) {
        const storedVoucherId = localStorage.getItem('klarnaVoucherId');
        if (storedVoucherId) {
          voucherIdForFinalize = storedVoucherId;
        }
      }
      if (voucherIdForFinalize && voucherIdForFinalize > 0) {
        formData.append('voucherId', voucherIdForFinalize);
      }
      // Ajouter les crédits utilisés si présents
      let creditsForFinalize = 0;
      if (isKlarnaSelected.value) {
        const storedCredits = localStorage.getItem('creditsToUse');
        creditsForFinalize = storedCredits ? parseFloat(storedCredits) : (creditsToUse.value || 0);
      } else {
        creditsForFinalize = creditsToUse.value || 0;
      }
      if (creditsForFinalize > 0) {
        formData.append('credits', creditsForFinalize);
      }

      // Utiliser l'offerID sauvegardé comme fallback pour Klarna
      const klarnaOfferId = offerData.offerId || localStorage.getItem('klarnaOfferID');
      const paymentEndpoint = isKlarnaSelected.value
        ? `https://www.ouicsport.fr/api/search/process-payment-Klarna/${klarnaOfferId}`
        : `https://www.ouicsport.fr/api/search/process-payment/${offerID.value}`;

      try {
        const response = await axios.post(paymentEndpoint, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        paymentSuccess.value = true;
        paymentDetails.value = response.data.data;
        

        if (!isKlarnaSelected.value) {
          localStorage.setItem('offerDetails', JSON.stringify(offerDetails.value));
          localStorage.setItem('quantity', quantity.value);
          localStorage.setItem('variation', JSON.stringify(offerDetails.value.criterion.map(option => option.Value)));
          localStorage.setItem('equipmentQuantities',  equipementJSON.value);
        }

        // Appeler NOTIFY pour l'assurance Chapka si souscrite
        const chapkaData = localStorage.getItem('chapkaInsurance');
        if (chapkaData) {
          try {
            const insurance = JSON.parse(chapkaData);
            if (insurance.accepted && insurance.participants && insurance.participants.length > 0) {
              // PRIORITÉ: purchaseId (retourné par l'API) > id (attention, peut être userId) > paymentIntentId
              // Correction: ne pas utiliser .id si c'est potentiellement l'userId (5296)
              // On cherche explicitement purchaseId ou reservationId dans la réponse
              const purchaseId = response.data.data?.purchaseId || response.data.purchaseId || response.data.data?.reservationId;
              
              // Fallback sur paymentIntentId seulement si on n'a pas d'ID de réservation explicite
              const reservationId = purchaseId || paymentIntentId;
              

              const userEmail = store.state.auth.user.data.email;
              
              const notifyResult = await notifyInsurance({
                reservationId: String(reservationId),
                email: userEmail,
                formule: insurance.formule || 'AN',
                produit: insurance.produit || 'CAP-EXPLORER',
                provenance: 'FR',
                destination: 'FR',
                nombre: insurance.participants.length,
                depart: insurance.dates.depart,
                retour: insurance.dates.retour,
                date_resa: new Date().toISOString().split('T')[0],
                montant_total: insurance.totalAmount || (offerDetails.value.price * quantity.value),
                prime: insurance.premium.toString(),
                participants: insurance.participants
              });
              
              if (notifyResult.success) {
                localStorage.setItem('chapkaSubscribed', JSON.stringify({
                  success: true,
                  chapkaId: notifyResult.chapkaId
                }));
              } else {
                console.error('[Chapka] Erreur lors de la souscription:', notifyResult.error);
                // On continue quand même, la réservation est valide
                localStorage.setItem('chapkaSubscribed', JSON.stringify({
                  success: false,
                  error: notifyResult.error
                }));
              }
            }
          } catch (chapkaError) {
            console.error('[Chapka] Erreur traitement assurance:', chapkaError);
            // Ne pas bloquer la réservation
          }
        }

        // Nettoyage des données Klarna du localStorage
        if (isKlarnaSelected.value) {
          localStorage.removeItem('isKlarnaSelected');
          localStorage.removeItem('klarnaOfferID');
          localStorage.removeItem('klarnaVoucher');
          localStorage.removeItem('klarnaVoucherId');
          localStorage.removeItem('creditsToUse');
        }

        router.push({ name: 'ConfirmationPage' });
      } catch (err) {
        console.error('Erreur processPayment:', err);
        paymentError.value = 'Un problème est survenu lors de la confirmation du paiement.';
        loadingPayment.value = false;
      }
    };
        const fetchCreditsBalance = async () => {
          try {
            const response = await axios.get(`https://www.ouicsport.fr/api/profile/credits-balance?userId=${store.state.auth.user.data.id}`);
            creditsBalance.value = response.data.data; 
          } catch (error) {
            console.error('Erreur récupération du solde de crédits :', error);
            creditsBalance.value = 0;
          }
        };

        const useCredits = async ()  => {
          const total = Number(totalPrice.value || 0);
          const candidate = Math.min(Number(creditsBalance.value || 0), total);
          if (candidate <= 0) {
            return;
          }
          // 100% crédits
          if (candidate >= total) {
            const dateParts = offerDetails.value.selected_date.split(" ")[0].split("/");
            const formattedDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
            const formData = new FormData();
            formData.append('userId', store.state.auth.user.data.id);
            formData.append('quantity', quantity.value);
            formData.append('date', formattedDate);
            formData.append('dateFull', localStorage.getItem('formattedDates'));
            formData.append('variations', JSON.stringify(offerDetails.value.criterion.map(option => decodeHtmlEntities(option.Value))));
            formData.append('credits', total);
            if (equipementJSON.value != null) {
              formData.append('selectedEquipments', equipementJSON.value);
            }
            if (voucher.value > 0) {
              formData.append('voucherId', voucherId.value);
            }
            const paymentEndpoint = `https://www.ouicsport.fr/api/search/process-payment/${offerID.value}`;
            const response = await axios.post(paymentEndpoint, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            paymentSuccess.value = true;
            paymentDetails.value = response.data.data;
            localStorage.setItem('offerDetails', JSON.stringify(offerDetails.value));
            localStorage.setItem('quantity', quantity.value);
            localStorage.setItem('variation', JSON.stringify(offerDetails.value.criterion.map(option => option.Value)));
            localStorage.setItem('equipmentQuantities',  equipementJSON.value);
            router.push({ name: 'ConfirmationPage' });
            return;
          }
          // Partiel (crédits + CB)
          creditsToUse.value = candidate;
          let equipmentObj = null;
          if (equipementJSON.value) {
            try {
              const arr = JSON.parse(equipementJSON.value);
              equipmentObj = arr.reduce((acc, item) => {
                acc[item.name] = { quantity: item.quantity, price: item.price };
                return acc;
              }, {});
            } catch (e) {
              equipmentObj = null;
            }
          }
          await createPaymentIntent(equipmentObj || {});
        };
        fetchCreditsBalance();

        const voucherActivate = async (promoCode) => {
          try {
            voucherError.value = '';

            let total = quantity.value * (offerDetails.value && offerDetails.value.price ? parseFloat(offerDetails.value.price) : 0);

            if (equipementJSON.value != null) {
              const equipmentQuantities = JSON.parse(equipementJSON.value);
              for (const equipment of equipmentQuantities) {
                total += parseFloat(equipment.price) * parseInt(equipment.quantity);
              }
            }

            const totalPrice = total;

            const params = {
              offerId: offerID.value,
              promoCode: promoCode,
              amount: totalPrice,
              userId: store.state.auth.user.data.id
            };

            const response = await axios.get('https://www.ouicsport.fr/api/search/voucher', { params });
            
            voucher.value = response.data.percentage;
            voucherId.value = response.data.id;
            voucherMessage.value = 'Bon de réduction appliqué :' + voucher.value + '%';
          } catch (error) {
            console.error('Erreur récupération du bon de réduction :', error);

            voucher.value = 0;
            voucherId.value = null;

            if (error.response && error.response.data && error.response.data.message) {
              voucherError.value = error.response.data.message;
            } else {
              voucherError.value = "Une erreur est survenue lors de l'application du code.";
            }
          }
        };
        const calculateTotal = () => {
          const maxQty = parseInt(offerDetails.value?.quantite, 10) || 0;
          quantity.value = Math.min(Math.max(quantity.value, 0), maxQty);

          let total = quantity.value 
            * (offerDetails.value?.price 
              ? parseFloat(offerDetails.value.price) 
              : 0);
              
          if (equipementJSON.value != null) {
            const equipmentQuantities = JSON.parse(equipementJSON.value);
            for (const equipment of equipmentQuantities) {
              total += parseFloat(equipment.price) * parseInt(equipment.quantity, 10);
            }
          }

          if (voucher.value > 0) {
            total *= (1 - voucher.value / 100);
          }

          // Ajouter la prime d'assurance si acceptée
          if (insuranceAccepted.value && insurancePremium.value > 0) {
            total += parseFloat(insurancePremium.value);
          }

          totalPrice.value = total;
        };
        
        // Gestion de l'assurance Chapka
        const handleInsuranceUpdate = (data) => {
          insuranceAccepted.value = data.accepted;
          insurancePremium.value = data.premium || 0;
          insuranceFormule.value = data.formule || 'AN';
          insuranceProduit.value = data.produit || 'CAP-EXPLORER';
          insuranceQuote.value = data.quote;
          calculateTotal();
        };
        
        const handleParticipantsUpdate = (data) => {
          insuranceParticipants.value = data.participants;
          insuranceParticipantsValid.value = data.isValid;
        };
        
        const handleQuoteLoaded = (quote) => {
          insuranceQuote.value = quote;
        };
        
        // Calculer les dates pour l'assurance
        const computeInsuranceDates = () => {
          if (offerDetails.value && offerDetails.value.criteria) {
            const dates = extractDatesFromCriteria(offerDetails.value.criteria);
            insuranceDates.value = dates;
          }
        };


        const voucherActivateFront = async (code) => {
          await voucherActivate(code);
          calculateTotal();
        };

        // Prix d'affichage calculé à partir des critères de la date (comme groupPrice dans OffersList)
        const displayPrice = computed(() => {
          if (offerDetails.value && offerDetails.value.price) {
            return offerDetails.value.price;
          }
          if (!selectedDetail.value || selectedDetail.value.length === 0) {
            return null;
          }
          const prices = selectedDetail.value.map(c => parseFloat(c.price)).filter(p => !isNaN(p));
          if (prices.length === 0) return null;
          const minPrice = Math.min(...prices);
          const maxPrice = Math.max(...prices);
          if (minPrice === maxPrice) {
            return minPrice.toFixed(2);
          }
          return `${minPrice.toFixed(2)}-${maxPrice.toFixed(2)}`;
        });

    const onShare = async () => {
      const title = offerDetails.value?.offerDetails?.titleOffers
        || `${offerDetails.value?.offerDetails?.type || 'Activité'} à ${offerDetails.value?.offerDetails?.finalCity || ''}`;
      const text = `Découvre cette activité sur OuiCSport`;
      const url = `https://www.ouicsport.fr/offer/${offerID.value}`;
      await shareOffer({ title, text, url });
    };

     return {voucherMessage,noCritrion,displayPrice, totalPrice, remainingAmount, creditsToUse, calculateTotal, voucherError, voucherActivateFront, useCredits, creditsBalance, offerDetails, loading, error, isLoggedIn,currentMonth, currentYear, quantity, selectedDetail,  createPaymentIntent,
  setupStripe,
  submitPayment,
  processPayment, loadingPayment,
  clientSecret, SetupEquipementJSON,
  publicKey, paymentSuccess, paymentDetails, paymentError,formattedDates,dateSelected , googleDriveLinkFormatted, isKlarnaSelected,
  apiImg,
  sportBanner,
  isNative,
  onShare,
  // Assurance Chapka
  insuranceAccepted,
  insurancePremium,
  insuranceFormule,
  insuranceProduit,
  insuranceQuote,
  insuranceParticipants,
  insuranceParticipantsValid,
  insuranceDates,
  handleInsuranceUpdate,
  handleParticipantsUpdate,
  handleQuoteLoaded,
  computeInsuranceDates};
 },
 methods: {
  onQuantityFocus() {
    if (this.quantity === 0) {
      this.quantity = null;
    }
  },
  checkScreenWidth() {
      if (window.innerWidth < 768) {
        this.isMenuVisible = true;
      } else {
        this.isMenuVisible = false;
      }
    },
    observeReservationForm() {
        const observer = new MutationObserver(() => {
          const reservationForm = document.querySelector('.reservation-form');
          if (reservationForm) {
            observer.disconnect(); 
            reservationForm.scrollIntoView({ behavior: 'smooth' });
          }
        });

        observer.observe(document.body, { childList: true, subtree: true });
      } ,
      openCriterionModal(selectedDetail) {
      this.modalOptions = selectedDetail;

      this.showCriterionModal = true; 
    },

    selectCriterionOption(option) {
      this.selectedCriteria = option;

      this.offerDetails.price = option.price;
      this.offerDetails.quantite = option.quantite;
      this.offerDetails.criterion = option.criterion;
      this.offerDetails.selected_date = option.date;

      this.updateVariations();

      this.showCriterionModal = false;
    },

    updateVariations() {
      if (this.selectedCriteria && this.selectedCriteria.criterion) {
        this.variations = this.selectedCriteria.criterion
          .map(criterion => `${criterion.Value}`)
          .join(' / ');
        
      } else {
        this.variations = '';
      }
    },

  togglePaymentMethod() {
      this.isKlarnaSelected = !this.isKlarnaSelected;
      this.createPaymentIntent(this.equipmentQuantities);
    },
    findPriceByDate(searchDate) {
      const criteria = this.offerDetails.criteria || [];
      const filteredCriteria = criteria.find(criterion => criterion.date === searchDate)
                            || criteria.find(criterion => criterion.date.startsWith(searchDate + ' '));
      return filteredCriteria ? filteredCriteria.price : 'N/A';
    },
    findScheduleByDate(searchDate) {
      const criteria = this.offerDetails.criteria || [];
      const filteredCriteria = criteria.find(criterion => criterion.date === searchDate)
                            || criteria.find(criterion => criterion.date.startsWith(searchDate + ' '));
      
      if (!filteredCriteria) {
        return '';
      }

      const scheduleCriterion = filteredCriteria.criterion.find(c => c.Name === 'Horaire');
      
      if (!scheduleCriterion) {
        return '';
      }

      const [startTime, endTime] = scheduleCriterion.Value.split(' - ');
      return `de ${startTime} à ${endTime}`;
    },
  parseDate(dateStr) {
    const [datePart, timePart] = dateStr.split(' ');
    const [day, month, year] = datePart.split('/').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);

    return new Date(year, month - 1, day, hours, minutes);
  },

  isAfterToday(dateStr) {
    const today = new Date();
    today.setHours(0, 0, 0, 0); 
    const detailDate = this.parseDate(dateStr);
    return detailDate > today;
  },

  findEarliestFutureDate(dates) {
    let earliestDate = null;
    dates.forEach(dateStr => {
      const date = this.parseDate(dateStr);
      if ((date > new Date()) && (!earliestDate || date < earliestDate)) {
        earliestDate = date;
      }
    });

    return earliestDate ? this.formatDate(earliestDate) : null;
  },



  formatDate(date) {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return date.toLocaleDateString('fr-FR', options);
  },

  updateQuantity(equipmentName, quantity, price) {
    if (!this.equipmentQuantities[equipmentName]) {
      this.equipmentQuantities[equipmentName] = { quantity: 0, price: 0 };
    }

    const newQuantity = Math.min(parseInt(quantity, 10), this.quantity);

    this.equipmentQuantities[equipmentName].quantity = newQuantity;
    this.equipmentQuantities[equipmentName].price = newQuantity * parseFloat(price);
    this.SetupEquipementJSON(this.equipmentQuantities);
    this.calculateTotal();
  },
  
 
 initStripe() {
    this.createPaymentIntent(this.equipmentQuantities);
  },
  hasSelectedVariations() {
    return !!this.variations; 
  },
  hasSelectedQuantity() {
    return this.quantity > 0;
  },
  handleParentClick() {
    if (!this.accepteCGV || !this.hasSelectedQuantity() || !this.hasSelectedVariations) {
      this.handleDisabledClick();
    }
  },
  reserver() {
    if (this.accepteCGV && this.hasSelectedQuantity() && this.hasSelectedVariations) {
      // Vérifier si l'assurance est acceptée mais les participants ne sont pas valides
      if (this.insuranceAccepted && !this.insuranceParticipantsValid) {
        alert('Veuillez remplir les informations de tous les participants pour l\'assurance.');
        return;
      }
      this.showReservationForm = true;
      this.$nextTick(() => {
        window.scrollTo({ top: 0 });
        this.initStripe();
      });
    } else {
      this.handleDisabledClick();
    }
  },

  handleDisabledClick() {
    if (!this.accepteCGV) {
      this.showErrorAnimationCGV = true;
      setTimeout(() => { this.showErrorAnimationCGV = false; }, 1000);
    }
    if (!this.hasSelectedQuantity()) {
      this.showErrorAnimationQuantity = true;
      setTimeout(() => { this.showErrorAnimationQuantity = false; }, 1000);
    }
    if (!this.hasSelectedVariations()) {
      this.showErrorAnimationVariations = true;
      setTimeout(() => { this.showErrorAnimationVariations = false; }, 1000);
    }
  },
  

selectDetail(detail) {
 this.price = detail.price;
 this.selectedDetail = detail;
 this.selectedDetailUniqueProp = detail.id;
 this.showModal = false;

},


isStageSejour() {
 return this.offerDetails && this.offerDetails.offerDetails.type === "Stage à la carte" || this.offerDetails.offerDetails.type === "Séjour" || this.offerDetails.offerDetails.type === "Stage" ||  this.offerDetails.offerDetails.type === "Stage/Séjour";
}

 }
}


</script>

<style>
/* =========================================================================
   STYLES NATIFS iOS / ANDROID — OfferDetailsV2.vue
   Écran de réservation puis de paiement.

   Chaque sélecteur est préfixé par `body.is-native` : sur www.ouicsport.fr la
   classe n'existe pas, ces règles y sont donc totalement inertes.

   Ce que cette passe corrige :
   - le bouton RÉSERVER n'avait aucun fond déclaré (blanc sur gris système,
     illisible) : il reçoit le rouge de marque plein, et un état inactif
     contrasté ;
   - la barre prix + bouton devient une barre d'action ancrée, posée AU-DESSUS
     de la barre d'onglets (elle passait dessous et était inatteignable) ;
   - les champs adoptent le gabarit .oc-field (44px, trait neutre, anneau de
     focus de marque) au lieu des bordures rouges de 2px ;
   - un seul rouge (var(--oc-brand)) remplace les #e74c3c / #dd4124 / #c0392b
     qui cohabitaient ;
   - la fiche n'est plus tirée de -200px sous le bandeau ni bridée à 80% de
     large : elle occupe l'écran.
   ========================================================================= */

/* -------------------------------------------------------------------------
   1. Bandeau et enveloppe de page
   ------------------------------------------------------------------------- */

/* La barre d'application (.oc-appbar) est déjà en flux et gère l'encoche :
   le bandeau ne doit PAS ajouter une seconde fois la zone sûre haute. */
body.is-native .hero3 {
  position: relative;
  height: 176px;
  min-height: 176px;
  padding: 0;
  background-size: cover !important;
  background-position: center !important;
  background-color: var(--oc-surface-2);
}

/* Le site tire cette carte de -200px (-20% en dessous de 768px) sous le
   bandeau et la limite à 80% de large : sur téléphone, elle laissait un vide
   en bas de page et deux gouttières inutiles. */
body.is-native .activity-details {
  position: relative;
  top: 0;
  z-index: 10;
  display: block;
  width: 100%;
  max-width: none;
  box-sizing: border-box;
  margin: -20px 0 0;
  /* Réserve la barre d'action ancrée (la barre d'onglets, elle, est déjà
     réservée par le padding de .page-main). */
  padding: var(--oc-sp-5) var(--oc-sp-4) 104px;
  background: var(--oc-bg);
  border-radius: var(--oc-r-xl) var(--oc-r-xl) 0 0;
  box-shadow: none;
}

/* Bouton de partage : remonté sur le bandeau, zone tactile portée à 44px. */
body.is-native .activity-details > div[style*="position: absolute"] {
  top: -56px !important;
  right: var(--oc-sp-4) !important;
  z-index: 30 !important;
}

body.is-native .activity-details > div[style*="position: absolute"] button {
  display: flex !important;
  align-items: center;
  justify-content: center;
  width: var(--oc-tap) !important;
  height: var(--oc-tap) !important;
  color: var(--oc-ink) !important;
  background: var(--oc-surface) !important;
  border-radius: var(--oc-r-full) !important;
  box-shadow: var(--oc-e2) !important;
}

/* -------------------------------------------------------------------------
   2. En-tête de l'offre
   ------------------------------------------------------------------------- */

body.is-native .activity-details-top {
  display: block;
  grid-template-columns: none;
  box-sizing: border-box;
  margin: 0 0 var(--oc-sp-4);
  padding: var(--oc-sp-4);
  background: var(--oc-surface);
  border-radius: var(--oc-r-lg);
  box-shadow: var(--oc-e1);
}

/* Sport / date / ville : métadonnées, pas des titres. Le titre de l'offre
   (h3) reste le seul élément dominant du bloc. */
body.is-native .activity-details-top h2 {
  margin: 0 0 var(--oc-sp-1);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-sm);
  font-weight: 700;
  letter-spacing: 0.4px;
  line-height: 1.4;
  text-transform: uppercase;
  text-align: left !important; /* le second h2 impose text-align:right en ligne */
  color: var(--oc-ink-3);
}

body.is-native .activity-details-top h3 {
  margin: var(--oc-sp-2) 0 0;
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-title);
  font-weight: 400;
  line-height: 1.2;
  text-align: left;
  color: var(--oc-ink);
}

/* -------------------------------------------------------------------------
   3. Description de l'activité
   ------------------------------------------------------------------------- */

body.is-native .activity-details-description {
  display: block;
  grid-template-columns: none;
  width: 100%;
  box-sizing: border-box;
  margin: 0 0 var(--oc-sp-4);
  padding: var(--oc-sp-4);
  background: var(--oc-surface);
  border-radius: var(--oc-r-lg);
  box-shadow: var(--oc-e1);
}

body.is-native .activity-details-description .image_activity_left,
body.is-native .activity-details-description .image_activity {
  width: 100%;
  margin: var(--oc-sp-4) 0;
}

/* Le site plafonne la hauteur à 200px : sur téléphone l'image était rognée
   n'importe comment. Ratio fixe + object-fit à la place. */
body.is-native .activity-details-description .image_activity_left img,
body.is-native .activity-details-description .image_activity img {
  display: block;
  width: 100%;
  height: auto;
  max-height: none;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  border-radius: var(--oc-r-md);
}

body.is-native .activity-location {
  margin: 0 0 var(--oc-sp-3);
  padding-bottom: 0;
  font-size: var(--oc-text-base);
  font-weight: 600;
  line-height: 1.45;
  color: var(--oc-ink-2);
}

body.is-native .activity-location a {
  color: var(--oc-brand);
  font-weight: 700;
}

body.is-native .p-description-wire {
  padding: 0;
  font-size: var(--oc-text-base);
  line-height: 1.6;
  color: var(--oc-ink-2);
}

body.is-native .activity-details-description iframe {
  display: block;
  width: 100% !important;
  height: 320px !important;
  margin: var(--oc-sp-4) 0;
  border: 0;
  border-radius: var(--oc-r-md);
}

body.is-native .tariff-information {
  display: block;
  grid-template-columns: none;
  box-sizing: border-box;
  margin: var(--oc-sp-4) 0 0;
  padding: var(--oc-sp-4);
  background: var(--oc-brand-50);
  border-radius: var(--oc-r-lg);
}

body.is-native .tariff-information .price-tag h2 {
  margin: 0;
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-title);
  font-weight: 400;
  color: var(--oc-brand-700);
}

/* -------------------------------------------------------------------------
   4. Formulaire de réservation — gabarit .oc-field
   ------------------------------------------------------------------------- */

body.is-native .reservation-form {
  display: block;
  grid-template-columns: 1fr;
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  padding: var(--oc-sp-4);
  background: var(--oc-surface);
  border-radius: var(--oc-r-lg);
  box-shadow: var(--oc-e1);
}

body.is-native .reservation-form .form-section {
  display: block;
  width: 100%;
  margin: 0 0 var(--oc-sp-5);
  padding: 0;
}

body.is-native .reservation-form label {
  display: block;
  margin: 0 0 6px;
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-sm);
  font-weight: 700;
  color: var(--oc-ink-2);
}

/* Sélecteurs de date et d'options : contrôles de formulaire, plus des pavés
   rouges. Le rouge est réservé à l'action finale. */
body.is-native .reservation-form .btn,
body.is-native .reservation-form .btn.date-range,
body.is-native .reservation-form .btn.time {
  display: block;
  box-sizing: border-box;
  width: 100%;
  max-width: none;
  min-height: var(--oc-tap);
  margin: 0;
  padding: var(--oc-sp-3) var(--oc-sp-4);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-base);
  font-weight: 600;
  text-align: left;
  color: var(--oc-ink);
  background: var(--oc-surface);
  border: 1px solid var(--oc-line-2);
  border-radius: var(--oc-r-md);
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  transition: border-color var(--oc-fast) var(--oc-ease),
              box-shadow var(--oc-fast) var(--oc-ease);
}

body.is-native .reservation-form .btn:focus {
  outline: none;
  border-color: var(--oc-brand);
  box-shadow: 0 0 0 3px var(--oc-brand-100);
}

/* Option choisie : état actif discret (le bouton RÉSERVER doit rester le
   seul aplat rouge de l'écran). */
body.is-native .reservation-form .btn.btn-selected {
  font-weight: 700;
  color: var(--oc-brand-700);
  background: var(--oc-brand-50);
  border-color: var(--oc-brand-200);
}

body.is-native .reservation-form .btn.btn-grey {
  color: var(--oc-ink-2);
  background: var(--oc-surface);
  border-color: var(--oc-line-2);
}

/* Sélecteur de quantité : pilule 44px, boutons ± lisibles. */
body.is-native .quantity-options {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--oc-sp-3);
  width: 100%;
  margin: 0;
}

body.is-native .flex-input-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  height: auto;
  min-height: var(--oc-tap);
  overflow: hidden;
  background: var(--oc-surface);
  border: 1px solid var(--oc-line-2);
  border-radius: var(--oc-r-full);
}

body.is-native .flex-input-container input {
  width: 56px;
  height: var(--oc-tap);
  margin: 0;
  padding: 0;
  font-size: var(--oc-text-base);
  font-weight: 700;
  line-height: 1;
  text-align: center;
  color: var(--oc-ink);
  background: transparent;
  border: 0;
}

body.is-native .button-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--oc-tap);
  height: var(--oc-tap);
  min-width: var(--oc-tap);
  padding: 0;
  font-size: var(--oc-text-lg);
  font-weight: 700;
  color: var(--oc-brand);
  background: var(--oc-surface);
  border: 0;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
}

body.is-native .button-selector:disabled {
  color: var(--oc-ink-3);
  background: var(--oc-surface-2);
  opacity: 1;
}

body.is-native .button-selector:not(:disabled):active {
  background: var(--oc-brand-50);
}

body.is-native input.quantity-input,
body.is-native input.no-arrows {
  box-sizing: border-box;
  min-width: 0;
  font-size: var(--oc-text-base);
  color: var(--oc-ink);
}

/* Options payantes : le libellé passe au-dessus de son sélecteur, sinon il
   se retrouvait seul sur une ligne entre les deux. */
body.is-native .form-section.options .quantity-options {
  margin-bottom: var(--oc-sp-4);
}

body.is-native .form-section.options .quantity-options .option-name {
  order: -1;
  flex: 1 1 100%;
  margin: 0;
  font-size: var(--oc-text-base);
  font-weight: 600;
  color: var(--oc-ink);
}

body.is-native .form-section.options .quantity-options .price,
body.is-native .form-section.quantity .price {
  margin-left: auto;
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-lg);
  font-weight: 400;
  color: var(--oc-brand);
}

body.is-native .form-section.insurance {
  margin: 0 0 var(--oc-sp-5);
  padding: 0;
}

/* Total */
body.is-native .total-line {
  height: 1px;
  margin: var(--oc-sp-4) 0;
  background: var(--oc-line);
}

body.is-native .total-amount {
  margin: 0 0 var(--oc-sp-4);
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-title);
  font-weight: 400;
  text-align: right;
  color: var(--oc-ink); /* le site utilise #95a6a6 : 2,4:1, sous le seuil AA */
}

body.is-native .insurance-included {
  margin: calc(-1 * var(--oc-sp-2)) 0 var(--oc-sp-4);
  padding: 0;
  text-align: right;
}

body.is-native .insurance-label-total {
  font-size: var(--oc-text-sm);
  font-style: normal;
  color: var(--oc-ink-3);
}

/* CGV */
body.is-native .form-section.cgv {
  display: flex;
  align-items: flex-start;
  gap: var(--oc-sp-3);
  box-sizing: border-box;
  padding: var(--oc-sp-3);
  background: var(--oc-surface-2);
  border-radius: var(--oc-r-md);
}

body.is-native .form-section.cgv input[type="checkbox"] {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  margin: 2px 0 0;
  accent-color: var(--oc-brand);
}

body.is-native .form-section.cgv label {
  margin: 0;
  font-size: var(--oc-text-base);
  font-weight: 500;
  line-height: 1.4;
  color: var(--oc-ink);
}

body.is-native .form-section.cgv .cgv-link {
  display: flex;
  align-items: center;
  min-height: var(--oc-tap);
  margin-top: var(--oc-sp-1);
  font-weight: 700;
  color: var(--oc-brand);
  text-decoration: underline;
}

/* -------------------------------------------------------------------------
   5. Barre d'action « RÉSERVER »
   -------------------------------------------------------------------------
   Ancrée au-dessus de la barre d'onglets : à bottom:0 elle passait dessous. */

body.is-native .reservation-button.oc-actionbar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: calc(var(--oc-tabbar-h) + var(--oc-safe-bottom));
  z-index: 940;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--oc-sp-3);
  box-sizing: border-box;
  width: auto;
  margin: 0;
  padding: var(--oc-sp-3) calc(var(--oc-sp-4) + var(--oc-safe-right))
           var(--oc-sp-3) calc(var(--oc-sp-4) + var(--oc-safe-left));
  background: var(--oc-surface);
  border-top: 1px solid var(--oc-line);
  box-shadow: var(--oc-e-up);
}

/* CORRECTIF : aucun fond n'était déclaré ici ni dans style.css (qui ne pose
   que color:#fff). Le bouton héritait du gris système du moteur de rendu :
   texte blanc sur gris clair, illisible. */
body.is-native .reservation-button button {
  flex: 1 1 auto;
  display: block;
  box-sizing: border-box;
  width: auto;
  min-width: 0;
  min-height: var(--oc-tap);
  padding: var(--oc-sp-3) var(--oc-sp-5);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-base);
  font-weight: 700;
  letter-spacing: 0.4px;
  color: var(--oc-brand-ink);
  background: var(--oc-brand);
  border: 0;
  border-radius: var(--oc-r-full);
  box-shadow: var(--oc-e1);
  cursor: pointer;
  transition: background var(--oc-fast) var(--oc-ease),
              transform var(--oc-fast) var(--oc-ease);
}

body.is-native .reservation-button button.btn-selected {
  color: var(--oc-brand-ink);
  background: var(--oc-brand);
}

body.is-native .reservation-button button:not(:disabled):active {
  background: var(--oc-brand-600);
  transform: scale(0.985);
}

/* État « pas encore réservable » : le bouton reste cliquable (il déclenche
   l'animation qui désigne le champ manquant), mais il ne doit plus se
   confondre avec l'action disponible. */
body.is-native .reservation-button button.btn-grey {
  color: var(--oc-ink-2);
  background: var(--oc-surface-2);
  box-shadow: none;
}

body.is-native .reservation-button button:disabled {
  color: var(--oc-ink-3);
  background: var(--oc-surface-2);
  box-shadow: none;
  opacity: 1;
}

body.is-native .reservation-button .oc-actionbar__price {
  flex: 0 0 auto;
}

/* -------------------------------------------------------------------------
   6. Modales (date, options) — plein écran
   ------------------------------------------------------------------------- */

/* Les styles en ligne du composant (display, position, fond) imposent
   !important ici. */
body.is-native .reservation-form .modal {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  z-index: 1100 !important;
  display: flex !important;
  align-items: stretch;
  justify-content: stretch;
  width: 100% !important;
  height: 100% !important;
  background-color: rgba(26, 22, 20, 0.55) !important;
}

body.is-native .reservation-form .modal .modal-content {
  box-sizing: border-box;
  width: 100%;
  max-width: none;
  height: 100%;
  max-height: none;
  margin: 0;
  padding: calc(var(--oc-safe-top) + var(--oc-sp-4))
           calc(var(--oc-sp-4) + var(--oc-safe-right))
           calc(var(--oc-safe-bottom) + var(--oc-sp-6))
           calc(var(--oc-sp-4) + var(--oc-safe-left));
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: var(--oc-surface);
  border-radius: 0;
  box-shadow: none;
}

/* Fermeture : 44px, contrastée, et cible du bouton retour Android
   (attribut data-oc-dismiss posé dans le gabarit). */
body.is-native .reservation-form .modal .close {
  position: sticky;
  top: 0;
  float: right;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--oc-tap);
  height: var(--oc-tap);
  font-size: 26px;
  line-height: 1;
  color: var(--oc-ink);
  background: var(--oc-surface-2);
  border-radius: var(--oc-r-full);
  z-index: 10;
}

body.is-native .reservation-form .modal .modal-content ul {
  margin: calc(var(--oc-tap) + var(--oc-sp-3)) 0 0;
  padding: 0;
}

body.is-native .reservation-form .modal .modal-content li {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--oc-sp-1);
  min-height: 56px;
  padding: var(--oc-sp-3) var(--oc-sp-2);
  font-size: var(--oc-text-base);
  border-bottom: 1px solid var(--oc-line);
}

/* Le composant impose color:#ccc en ligne aux options complètes (1,6:1). */
body.is-native .reservation-form .modal .modal-content li.option-disabled {
  color: var(--oc-ink-3) !important;
  background: var(--oc-surface-2);
}

/* -------------------------------------------------------------------------
   7. Écran de paiement
   ------------------------------------------------------------------------- */

body.is-native .payment-form {
  display: block;
  box-sizing: border-box;
  width: 100%;
  margin: 0;
  padding: 0;
  font-family: var(--oc-font-text);
  color: var(--oc-ink);
  background: transparent;
  border-radius: 0;
  box-shadow: none;
}

body.is-native .payment-form .form-section {
  display: block;
  box-sizing: border-box;
  width: 100%;
  margin: 0 0 var(--oc-sp-4);
}

body.is-native .payment-form .form-section.details,
body.is-native .payment-form .form-section.credits,
body.is-native .payment-form .form-section.card-payment {
  margin-top: 0;
  padding: var(--oc-sp-4);
  background: var(--oc-surface);
  border-radius: var(--oc-r-lg);
  box-shadow: var(--oc-e1);
}

body.is-native .payment-form .form-section h2,
body.is-native .payment-form .form-section h3 {
  margin: 0 0 var(--oc-sp-3);
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-lg);
  font-weight: 400;
  letter-spacing: 0.3px;
  color: var(--oc-ink);
}

body.is-native .payment-form .details-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--oc-sp-2);
  margin: 0 0 var(--oc-sp-3);
}

/* Récapitulatif : ces champs sont désactivés, ce sont des étiquettes. */
body.is-native .payment-form .details-row input.btn.quantity-input {
  flex: 0 0 auto;
  box-sizing: border-box;
  width: auto;
  min-height: 40px;
  margin: 0;
  padding: var(--oc-sp-2) var(--oc-sp-3);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-base);
  font-weight: 600;
  text-align: center;
  color: var(--oc-ink-2);
  -webkit-text-fill-color: var(--oc-ink-2);
  background: var(--oc-surface-2);
  border: 0;
  border-radius: var(--oc-r-sm);
  opacity: 1;
}

body.is-native .payment-form .details-row .price {
  margin-left: auto;
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-lg);
  font-weight: 400;
  color: var(--oc-brand);
}

body.is-native .payment-form .details-row.insurance-recap {
  box-sizing: border-box;
  margin: var(--oc-sp-3) 0;
  padding: var(--oc-sp-3);
  background: var(--oc-brand-50);
  border: 1px solid var(--oc-brand-100);
  border-radius: var(--oc-r-md);
}

body.is-native .insurance-name {
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-base);
  color: var(--oc-brand-700);
}

body.is-native .insurance-tag {
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-xs);
  color: var(--oc-success);
  background: var(--oc-success-bg);
  border-radius: var(--oc-r-sm);
}

body.is-native .details-row .insurance-price {
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-lg);
  color: var(--oc-brand);
}

body.is-native .payment-form .divider {
  height: 1px;
  margin: var(--oc-sp-4) 0;
  background: var(--oc-line);
}

body.is-native .payment-form .total-amount {
  margin: 0;
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-title);
  text-align: right;
  color: var(--oc-ink);
}

/* Code promo : le site place le bouton en absolu dans un conteneur
   width:max-content ; sur téléphone il chevauchait le champ. */
body.is-native .form-section.promo-code {
  display: flex;
  align-items: center;
  gap: var(--oc-sp-2);
  width: 100%;
  margin-bottom: var(--oc-sp-2);
}

body.is-native .form-section.promo-code .promo-input {
  flex: 1 1 auto;
  box-sizing: border-box;
  width: auto;
  min-width: 0;
  max-width: none;
  min-height: var(--oc-tap);
  margin: 0;
  padding: 0 var(--oc-sp-4);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-base);
  color: var(--oc-ink);
  background: var(--oc-surface);
  border: 1px solid var(--oc-line-2);
  border-radius: var(--oc-r-md);
  -webkit-appearance: none;
  appearance: none;
}

body.is-native .form-section.promo-code .promo-input:focus {
  outline: none;
  border-color: var(--oc-brand);
  box-shadow: 0 0 0 3px var(--oc-brand-100);
}

body.is-native .form-section.promo-code .validate {
  position: static;
  flex: 0 0 auto;
  height: var(--oc-tap);
  min-height: var(--oc-tap);
  padding: 0 var(--oc-sp-4);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-sm);
  font-weight: 700;
  color: var(--oc-brand-700);
  background: var(--oc-brand-50);
  border: 0;
  border-radius: var(--oc-r-full);
  cursor: pointer;
}

body.is-native .form-section.promo-code .validate:disabled {
  color: var(--oc-ink-3);
  background: var(--oc-surface-2);
}

body.is-native .promo-login-hint {
  margin: 0 0 var(--oc-sp-4);
  font-size: var(--oc-text-sm);
  text-align: left;
}

body.is-native .promo-login-hint a {
  color: var(--oc-brand);
  font-weight: 700;
}

body.is-native .error-text,
body.is-native .error-message {
  margin: 0 0 var(--oc-sp-3);
  padding: var(--oc-sp-3);
  font-size: var(--oc-text-sm);
  font-weight: 600;
  color: var(--oc-danger);
  background: var(--oc-danger-bg);
  border: 0;
  border-left: 3px solid var(--oc-danger);
  border-radius: var(--oc-r-sm);
}

body.is-native .success-text {
  margin: 0 0 var(--oc-sp-3);
  padding: var(--oc-sp-3);
  font-size: var(--oc-text-sm);
  font-weight: 600;
  color: var(--oc-success);
  background: var(--oc-success-bg);
  border-radius: var(--oc-r-sm);
}

/* Crédits : action secondaire, elle ne doit pas concurrencer « PAYER ». */
body.is-native .form-section.credits .credits-info {
  margin: 0 0 var(--oc-sp-3);
  font-size: var(--oc-text-base);
  color: var(--oc-ink-2);
}

body.is-native .form-section.credits .btn.use-credits {
  box-sizing: border-box;
  width: 100%;
  max-width: none;
  min-height: var(--oc-tap);
  margin: 0;
  padding: 0 var(--oc-sp-5);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-base);
  font-weight: 700;
  color: var(--oc-brand-700);
  background: var(--oc-brand-50);
  border: 0;
  border-radius: var(--oc-r-full);
  cursor: pointer;
}

body.is-native .form-section.credits .btn.use-credits:disabled {
  color: var(--oc-ink-3);
  background: var(--oc-surface-2);
  cursor: not-allowed;
  opacity: 1;
}

body.is-native .remaining-amount {
  margin: 0;
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-base);
  font-weight: 700;
  color: var(--oc-ink);
}

body.is-native .reservation-section {
  width: 100%;
  margin: 0 0 var(--oc-sp-4);
}

/* Champ carte Stripe : #card-element et sa structure interne restent
   intacts, seule l'enveloppe est habillée. */
body.is-native .stripe-element,
body.is-native #card-element {
  box-sizing: border-box;
  width: 100%;
  margin: 0;
  padding: var(--oc-sp-4);
  background: var(--oc-surface);
  border: 1px solid var(--oc-line-2);
  border-radius: var(--oc-r-md);
  transition: border-color var(--oc-fast) var(--oc-ease),
              box-shadow var(--oc-fast) var(--oc-ease);
}

body.is-native #card-element.StripeElement--focus {
  border-color: var(--oc-brand);
  box-shadow: 0 0 0 3px var(--oc-brand-100);
}

body.is-native #card-element.StripeElement--invalid {
  border-color: var(--oc-danger);
}

body.is-native .klarna-message {
  box-sizing: border-box;
  width: 100%;
  padding: var(--oc-sp-4);
  font-size: var(--oc-text-sm);
  color: var(--oc-ink-2);
  background: var(--oc-surface);
  border: 1px solid var(--oc-line);
  border-radius: var(--oc-r-md);
}

body.is-native .form-section.installment {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--oc-sp-2);
}

body.is-native .stripe-button,
body.is-native .klarna-button {
  width: 100%;
  min-height: var(--oc-tap);
  padding: 0 var(--oc-sp-4);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-base);
  font-weight: 600;
  color: var(--oc-ink-2);
  background: var(--oc-surface);
  border: 1px solid var(--oc-line-2);
  border-radius: var(--oc-r-md);
  cursor: pointer;
}

body.is-native .KlarnaElement {
  display: flex;
  flex-direction: column;
  gap: var(--oc-sp-1);
}

body.is-native .installment-info {
  font-size: var(--oc-text-sm);
  color: var(--oc-ink-3);
}

/* -------------------------------------------------------------------------
   8. Barre d'action « PAYER »
   ------------------------------------------------------------------------- */

body.is-native .form-section.payment-button {
  position: fixed;
  left: 0;
  right: 0;
  bottom: calc(var(--oc-tabbar-h) + var(--oc-safe-bottom));
  z-index: 940;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--oc-sp-3);
  box-sizing: border-box;
  width: auto;
  margin: 0;
  padding: var(--oc-sp-3) calc(var(--oc-sp-4) + var(--oc-safe-right))
           var(--oc-sp-3) calc(var(--oc-sp-4) + var(--oc-safe-left));
  background: var(--oc-surface);
  border-top: 1px solid var(--oc-line);
  box-shadow: var(--oc-e-up);
}

body.is-native .form-section.payment-button .oc-actionbar__price {
  flex: 0 0 auto;
}

body.is-native .form-section.payment-button .btn.pay {
  flex: 1 1 auto;
  box-sizing: border-box;
  width: auto;
  max-width: none;
  min-width: 0;
  min-height: var(--oc-tap);
  padding: 0 var(--oc-sp-5);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-base);
  font-weight: 700;
  letter-spacing: 0.4px;
  color: var(--oc-brand-ink);
  background: var(--oc-brand);
  border: 0;
  border-radius: var(--oc-r-full);
  box-shadow: var(--oc-e1);
  cursor: pointer;
  transition: background var(--oc-fast) var(--oc-ease),
              transform var(--oc-fast) var(--oc-ease);
}

body.is-native .form-section.payment-button .btn.pay:not(:disabled):active {
  background: var(--oc-brand-600);
  transform: scale(0.985);
}

body.is-native .form-section.payment-button .btn.pay:disabled {
  color: var(--oc-ink-3);
  background: var(--oc-surface-2);
  box-shadow: none;
  cursor: not-allowed;
  opacity: 1;
}

/* -------------------------------------------------------------------------
   9. Filets de sécurité
   ------------------------------------------------------------------------- */

/* Zone tactile plancher. */
body.is-native .reservation-form button,
body.is-native .payment-form button,
body.is-native .reservation-form input[type="text"],
body.is-native .reservation-form input[type="number"] {
  min-height: var(--oc-tap);
}

/* Le voile de chargement couvre l'écran, encoche comprise. */
body.is-native .loading-overlay {
  background-color: var(--oc-bg);
}

body.is-native .spinner {
  width: 56px;
  height: 56px;
  border-width: 5px;
  border-color: var(--oc-line);
  border-top-color: var(--oc-brand);
}

/* =========================================================================
   TUNNEL DE RÉSERVATION — application mobile (préfixe rv-)
   Aucune de ces règles n'existe pour le site : toutes sont portées par
   body.is-native, et le balisage qu'elles habillent n'est rendu qu'en natif.
   ========================================================================= */

/* Remise à zéro au niveau des éléments. Indispensable : style.css peint les
   balises nues (header, h1, p, button, input, label…) et rattrape n'importe
   quel balisage neuf. `svg` est volontairement exclu — `all: unset` écrase
   ses attributs de présentation et transforme les tracés en aplats. */
body.is-native .rv-page header,
body.is-native .rv-page section,
body.is-native .rv-page h1,
body.is-native .rv-page p,
body.is-native .rv-page ul,
body.is-native .rv-page li,
body.is-native .rv-page span,
body.is-native .rv-page div,
body.is-native .rv-page button,
body.is-native .rv-page input,
body.is-native .rv-page label,
body.is-native .rv-page a,
body.is-native .rv-page strong {
  all: unset;
  display: revert;
  box-sizing: border-box;
}

body.is-native .rv-page {
  --rv-bg: #F5F0E6;
  --rv-surface: #EFE8DA;
  --rv-raised: #E5DDD0;
  --rv-dark: #2B231F;
  --rv-ink: #28201C;
  --rv-ink-2: #6B5E54;
  --rv-ink-3: #8C7E73;
  --rv-on-dark: #FAF6EE;
  --rv-red: #DD4124;
  --rv-line: rgba(107, 94, 84, 0.15);
  --rv-ko-bg: #FDECE8;
  --rv-ko: #C2341A;
  --rv-ko-line: #E89A90;
  --rv-ok-bg: #E7F5EE;
  --rv-ok: #1E8E5A;

  display: block;
  box-sizing: border-box;
  background: var(--rv-bg);
  font-family: var(--oc-font-text);
  color: var(--rv-ink);
  padding: 12px 12px calc(var(--oc-tabbar-h) + var(--oc-safe-bottom) + 88px) 12px;
}

/* ---------- Titre d'étape ---------- */

body.is-native .rv-page .rv-step {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 5px;
  margin-bottom: 14px;
  border-bottom: 2px solid var(--rv-ink);
}

body.is-native .rv-page .rv-step__num {
  font-family: var(--oc-font-display);
  font-size: 20px;
  line-height: 1;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

body.is-native .rv-page .rv-step__tag {
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--rv-on-dark);
  background: var(--rv-dark);
  border-radius: 8px;
  padding: 3px 8px;
}

/* ---------- Rappel de l'offre ---------- */

body.is-native .rv-page .rv-offer {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 12px;
}

body.is-native .rv-page .rv-offer__sport {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--rv-red);
}

body.is-native .rv-page .rv-offer__title {
  font-family: var(--oc-font-display);
  font-size: 21px;
  line-height: 1.12;
  text-transform: uppercase;
}

body.is-native .rv-page .rv-offer__meta {
  font-size: 13px;
  font-weight: 600;
  color: var(--rv-ink-2);
}

/* ---------- Rappel de la date ---------- */

body.is-native .rv-page .rv-when {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 12px;
  margin-bottom: 12px;
  border-radius: 14px;
  background: var(--rv-surface);
  border: 1px solid var(--rv-line);
  /* Elle ne doit pas ressembler aux blocs de choix qui suivent : pas de
     chevron, pas d'ombre, rien qui laisse croire qu'elle se déplie. */
  border-left: 3px solid var(--rv-red);
}

body.is-native .rv-page .rv-when--flat { border-left-color: var(--rv-ink-3); }

body.is-native .rv-page .rv-when__main {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

body.is-native .rv-page .rv-when__date {
  font-size: 14px;
  font-weight: 800;
  line-height: 1.25;
}

body.is-native .rv-page .rv-when__addr {
  font-size: 12px;
  font-weight: 600;
  color: var(--rv-ink-2);
  line-height: 1.3;
}

body.is-native .rv-page .rv-when__addr--link {
  text-decoration: underline;
  cursor: pointer;
  /* Le lien est du texte en ligne : la marge intérieure verticale agrandit
     la zone touchable sans changer la hauteur de la ligne. */
  padding: 12px 0;
  margin: -12px 0;
}

body.is-native .rv-page .rv-when__edit {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 800;
  color: var(--rv-red);
  text-decoration: underline;
  cursor: pointer;
  /* Cible tactile de 44px sans grossir le libellé. */
  padding: 12px 2px;
  margin: -12px 0;
}

/* ---------- Cartes de section ---------- */

body.is-native .rv-page .rv-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  margin-bottom: 12px;
  border-radius: 16px;
  background: var(--rv-surface);
  border: 1px solid var(--rv-line);
  transition: border-color 200ms ease, background-color 200ms ease;
}

body.is-native .rv-page .rv-card.is-error {
  background: var(--rv-ko-bg);
  border-color: var(--rv-ko-line);
}

body.is-native .rv-page .rv-card__label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

body.is-native .rv-page .rv-card__req {
  font-size: 11px;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0;
  color: var(--rv-red);
}

body.is-native .rv-page .rv-card__hint {
  font-size: 12px;
  font-weight: 600;
  color: var(--rv-ink-2);
}

/* ---------- Bouton de choix (critère) ---------- */

body.is-native .rv-page .rv-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-height: 46px;
  padding: 0 12px;
  border-radius: 12px;
  background: var(--rv-bg);
  border: 1.5px solid rgba(107, 94, 84, 0.18);
  font-size: 15px;
  font-weight: 700;
  color: var(--rv-ink);
  cursor: pointer;
  text-align: left;
  transition: border-color 160ms ease, transform 160ms ease;
}

body.is-native .rv-page .rv-select.is-empty { color: var(--rv-ink-3); }
body.is-native .rv-page .rv-select:active { transform: scale(0.99); border-color: var(--rv-red); }

/* ---------- Lignes quantité / options ---------- */

body.is-native .rv-page .rv-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 12px;
  background: var(--rv-bg);
}

body.is-native .rv-page .rv-line__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

body.is-native .rv-page .rv-line__name {
  font-size: 14px;
  font-weight: 800;
}

body.is-native .rv-page .rv-line__sub {
  font-size: 12px;
  font-weight: 600;
  color: var(--rv-ink-2);
}

body.is-native .rv-page .rv-step2 {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
}

/* 44px de cible tactile pour un bouton de 38px : le débord est absorbé par
   la marge négative, la ligne ne grandit pas. */
body.is-native .rv-page .rv-step2__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: -3px 0;
  border-radius: 12px;
  background: var(--rv-surface);
  border: 1.5px solid rgba(107, 94, 84, 0.18);
  font-size: 20px;
  font-weight: 800;
  line-height: 1;
  color: var(--rv-ink);
  cursor: pointer;
  transition: background-color 140ms ease, color 140ms ease, border-color 140ms ease;
}

body.is-native .rv-page .rv-step2__btn:active:not([disabled]) {
  background: var(--rv-red);
  border-color: var(--rv-red);
  color: var(--rv-on-dark);
}

body.is-native .rv-page .rv-step2__btn[disabled] {
  opacity: 0.35;
  cursor: default;
}

body.is-native .rv-page .rv-step2__val {
  min-width: 22px;
  text-align: center;
  font-size: 16px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

body.is-native .rv-page .rv-sub {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: var(--rv-ink-2);
}

body.is-native .rv-page .rv-sub__val {
  font-size: 16px;
  font-weight: 800;
  color: var(--rv-ink);
  font-variant-numeric: tabular-nums;
}

/* ---------- Assurance : la carte n'est qu'un contenant ---------- */

body.is-native .rv-page .rv-card--ins { padding: 12px; }

/* ---------- Total ---------- */

body.is-native .rv-page .rv-total { gap: 4px; }

body.is-native .rv-page .rv-total__row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
}

body.is-native .rv-page .rv-total__label {
  font-size: 16px;
  font-weight: 800;
}

body.is-native .rv-page .rv-total__val {
  font-family: var(--oc-font-display);
  font-size: 28px;
  line-height: 1;
  color: var(--rv-ink);
  font-variant-numeric: tabular-nums;
}

body.is-native .rv-page .rv-total__note {
  font-size: 12px;
  font-weight: 600;
  color: var(--rv-ink-2);
}

/* ---------- CGV ---------- */

body.is-native .rv-page .rv-cgv {
  padding: 6px 12px;
  margin-bottom: 12px;
  border-radius: 12px;
  background: var(--rv-bg);
  border: 1.5px solid var(--rv-line);
  transition: background-color 200ms ease, border-color 200ms ease;
}

body.is-native .rv-page .rv-cgv.is-error {
  background: var(--rv-ko-bg);
  border-color: var(--rv-ko-line);
}

body.is-native .rv-page .rv-cgv.is-error .rv-cgv__text { color: var(--rv-ko); }

body.is-native .rv-page .rv-cgv__label {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  cursor: pointer;
}

body.is-native .rv-page .rv-cgv__input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

body.is-native .rv-page .rv-cgv__box {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: var(--rv-surface);
  border: 2px solid rgba(107, 94, 84, 0.3);
  color: transparent;
  transition: background-color 140ms ease, border-color 140ms ease, color 140ms ease;
}

body.is-native .rv-page .rv-cgv__input:checked + .rv-cgv__box {
  background: var(--rv-red);
  border-color: var(--rv-red);
  color: var(--rv-on-dark);
}

body.is-native .rv-page .rv-cgv__input:focus-visible + .rv-cgv__box {
  outline: 2px solid var(--rv-red);
  outline-offset: 2px;
}

body.is-native .rv-page .rv-cgv__text {
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
}

body.is-native .rv-page .rv-cgv__link {
  display: inline-block;
  margin-left: 34px;
  padding: 6px 0 10px 0;
  font-size: 12px;
  font-weight: 700;
  color: var(--rv-red);
  text-decoration: underline;
  cursor: pointer;
}

/* ---------- Récapitulatif du paiement ---------- */

body.is-native .rv-page .rv-recap {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

body.is-native .rv-page .rv-recap__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--rv-ink-2);
}

body.is-native .rv-page .rv-recap__row--total {
  padding-top: 7px;
  border-top: 1px dashed rgba(107, 94, 84, 0.25);
  font-size: 15px;
  font-weight: 800;
  color: var(--rv-ink);
}

body.is-native .rv-page .rv-chip {
  display: inline-block;
  margin-left: 4px;
  padding: 2px 6px;
  border-radius: 5px;
  font-size: 10px;
  font-weight: 800;
  color: var(--rv-ok);
  background: var(--rv-ok-bg);
}

/* ---------- Code promo ---------- */

body.is-native .rv-page .rv-promo {
  display: flex;
  gap: 6px;
}

body.is-native .rv-page .rv-promo__input {
  flex: 1 1 auto;
  min-width: 0;
  height: 46px;
  padding: 0 12px;
  border-radius: 12px;
  background: var(--rv-bg);
  border: 1.5px solid rgba(107, 94, 84, 0.18);
  /* 16px : en dessous, iOS zoome au focus et casse la mise en page. */
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--rv-ink);
}

body.is-native .rv-page .rv-promo__input::placeholder { color: var(--rv-ink-3); }
body.is-native .rv-page .rv-promo__input:focus { border-color: var(--rv-red); outline: none; }
body.is-native .rv-page .rv-promo__input[disabled] { opacity: 0.55; }

body.is-native .rv-page .rv-promo__btn {
  flex: 0 0 auto;
  min-width: 86px;
  height: 46px;
  border-radius: 12px;
  background: var(--rv-dark);
  color: var(--rv-on-dark);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  text-align: center;
  cursor: pointer;
}

body.is-native .rv-page .rv-promo__btn[disabled] { opacity: 0.45; cursor: default; }

body.is-native .rv-page .rv-msg {
  font-size: 12px;
  font-weight: 700;
  padding: 8px 10px;
  border-radius: 10px;
}

body.is-native .rv-page .rv-msg--ok { color: var(--rv-ok); background: var(--rv-ok-bg); }
body.is-native .rv-page .rv-msg--ko { color: var(--rv-ko); background: var(--rv-ko-bg); }

/* ---------- Crédits ---------- */

body.is-native .rv-page .rv-credits {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px;
  margin-bottom: 12px;
  border-radius: 16px;
  background: var(--rv-dark);
  color: var(--rv-on-dark);
}

body.is-native .rv-page .rv-credits__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

body.is-native .rv-page .rv-credits__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--rv-ink-3);
}

body.is-native .rv-page .rv-credits__val {
  font-family: var(--oc-font-display);
  font-size: 22px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

body.is-native .rv-page .rv-credits__btn {
  flex: 0 0 auto;
  min-height: 44px;
  padding: 0 16px;
  border-radius: 22px;
  background: var(--rv-red);
  color: var(--rv-on-dark);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  cursor: pointer;
}

body.is-native .rv-page .rv-credits__btn[disabled] { opacity: 0.45; cursor: default; }

/* ---------- Carte bancaire ---------- */

body.is-native .rv-page .rv-cards {
  display: flex;
  gap: 4px;
}

body.is-native .rv-page .rv-cards span {
  padding: 2px 5px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 800;
  color: var(--rv-ink-2);
  background: var(--rv-raised);
}

body.is-native .rv-page .rv-due {
  font-size: 15px;
  font-weight: 600;
  color: var(--rv-ink-2);
}

body.is-native .rv-page .rv-due strong {
  font-weight: 800;
  color: var(--rv-ink);
  font-variant-numeric: tabular-nums;
}

/* Le champ carte est un iframe injecté par Stripe : seul ce conteneur est
   stylable, son contenu ne l'est que par l'API `appearance` de Stripe. Une
   hauteur fixe évite le saut de mise en page à l'injection. */
body.is-native .rv-page .rv-stripe {
  min-height: 52px;
  padding: 15px 12px;
  border-radius: 12px;
  background: var(--rv-bg);
  border: 1.5px solid rgba(107, 94, 84, 0.25);
}

/* ---------- Klarna (masqué en natif, prêt pour réactivation) ---------- */

body.is-native .rv-page .rv-klarna {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: 12px;
  margin-bottom: 12px;
  border-radius: 14px;
  background: var(--rv-bg);
  border: 1.5px solid rgba(107, 94, 84, 0.18);
  text-align: left;
  cursor: pointer;
}

body.is-native .rv-page .rv-klarna.is-on {
  border-color: #FFB3C7;
  background: #FFF0F4;
}

body.is-native .rv-page .rv-klarna__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

body.is-native .rv-page .rv-klarna__title { font-size: 15px; font-weight: 800; }

body.is-native .rv-page .rv-klarna__badge {
  padding: 2px 6px;
  border-radius: 5px;
  background: #FFB3C7;
  color: var(--rv-ink);
  font-size: 10px;
  font-weight: 800;
}

body.is-native .rv-page .rv-klarna__sub {
  font-size: 13px;
  font-weight: 600;
  color: var(--rv-ink-2);
  line-height: 1.3;
}

body.is-native .rv-page .rv-klarna-msg {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--rv-ink-2);
}

/* ---------- Erreur de paiement ---------- */

body.is-native .rv-page .rv-error {
  padding: 12px;
  margin-bottom: 12px;
  border-radius: 12px;
  background: var(--rv-ko-bg);
  border: 1.5px solid var(--rv-ko-line);
  color: var(--rv-ko);
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
}

/* ---------- Barre d'action ---------- */

body.is-native .rv-page .rv-bar {
  position: fixed;
  left: 0;
  right: 0;
  /* Posée au-dessus de la barre d'onglets, jamais par-dessus : la
     navigation principale reste atteignable. La zone sûre est déjà absorbée
     par .oc-tabbar, on ne la compte pas deux fois. */
  bottom: calc(var(--oc-tabbar-h) + var(--oc-safe-bottom));
  z-index: 940;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  height: 68px;
  padding: 0 14px;
  background: var(--rv-dark);
  border-top: 1px solid rgba(250, 246, 238, 0.12);
  box-shadow: 0 -4px 16px rgba(26, 22, 20, 0.25);
}

body.is-native .rv-page .rv-bar__price {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

body.is-native .rv-page .rv-bar__label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--rv-ink-3);
}

body.is-native .rv-page .rv-bar__val {
  font-family: var(--oc-font-display);
  font-size: 22px;
  line-height: 1;
  color: var(--rv-red);
  font-variant-numeric: tabular-nums;
}

body.is-native .rv-page .rv-bar__cta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  min-height: 46px;
  padding: 0 22px;
  border-radius: 23px;
  background: var(--rv-red);
  color: var(--rv-on-dark);
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
  transition: transform 160ms ease, opacity 160ms ease;
}

body.is-native .rv-page .rv-bar__cta:active:not([disabled]) { transform: scale(0.97); }

body.is-native .rv-page .rv-bar__cta[disabled] {
  opacity: 0.45;
  cursor: default;
  box-shadow: none;
}

body.is-native .rv-page .rv-bar__spin {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(250, 246, 238, 0.35);
  border-top-color: var(--rv-on-dark);
  border-radius: 50%;
  animation: rv-spin 800ms linear infinite;
}

@keyframes rv-spin { to { transform: rotate(360deg); } }

/* ---------- Feuille de choix ---------- */

body.is-native .rv-page .rv-sheet {
  position: fixed;
  inset: 0;
  z-index: 980;
  display: flex;
  align-items: flex-end;
  background: rgba(26, 22, 20, 0.6);
  animation: rv-fade 180ms ease both;
}

body.is-native .rv-page .rv-sheet__panel {
  width: 100%;
  max-height: 78vh;
  overflow-y: auto;
  padding: 14px 16px calc(var(--oc-safe-bottom) + 18px) 16px;
  border-radius: 24px 24px 0 0;
  background: var(--rv-bg);
  animation: rv-rise 240ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

body.is-native .rv-page .rv-sheet__grip {
  display: block;
  width: 34px;
  height: 4px;
  margin: 0 auto 12px auto;
  border-radius: 2px;
  background: var(--rv-raised);
}

body.is-native .rv-page .rv-sheet__title {
  font-family: var(--oc-font-display);
  font-size: 19px;
  text-transform: uppercase;
  margin-bottom: 12px;
}

body.is-native .rv-page .rv-sheet__close {
  width: 100%;
  justify-content: center;
  margin-top: 16px;
}

body.is-native .rv-page .rv-sheet__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

body.is-native .rv-page .rv-sheet__list > li {
  display: block;
  list-style: none;
}

body.is-native .rv-page .rv-sheet__opt {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  min-height: 52px;
  padding: 10px 12px;
  border-radius: 12px;
  background: var(--rv-surface);
  border: 2px solid transparent;
  font-size: 15px;
  font-weight: 800;
  color: var(--rv-ink);
  text-align: left;
  cursor: pointer;
}

body.is-native .rv-page .rv-sheet__opt:active:not([disabled]) { border-color: var(--rv-red); }

body.is-native .rv-page .rv-sheet__opt.is-full {
  opacity: 0.45;
  cursor: default;
}

body.is-native .rv-page .rv-sheet__state {
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 700;
  color: var(--rv-red);
}

body.is-native .rv-page .rv-sheet__opt.is-full .rv-sheet__state { color: var(--rv-ink-3); }

@keyframes rv-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes rv-rise { from { transform: translateY(100%); } to { transform: translateY(0); } }

@media (prefers-reduced-motion: reduce) {
  body.is-native .rv-page .rv-sheet,
  body.is-native .rv-page .rv-sheet__panel { animation: none; }
  body.is-native .rv-page .rv-bar__spin { animation-duration: 2s; }
}
</style>
